import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Sidebar } from '@/components/Sidebar';
import { useTheme } from '@/components/ThemeProvider';
import { CURRENCY_OPTIONS, currencySymbol, formatMoney } from '@/lib/currency';
import { DEFAULT_TIMEZONE, TIMEZONE_OPTIONS, timezoneLabel } from '@/lib/timezones';
import { Plus, Trash2, Save, CalendarPlus, CalendarMinus, Sun, Moon, Monitor, Users, Stethoscope, DoorOpen, Pencil, X, AlertCircle, RefreshCw, BellRing, Send, ExternalLink } from 'lucide-react';

const DAY_LABELS = {
  monday: 'Понедельник', tuesday: 'Вторник', wednesday: 'Среда',
  thursday: 'Четверг', friday: 'Пятница', saturday: 'Суббота', sunday: 'Воскресенье',
};
const DAYS_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const settingsTabFromLocation = () => {
  const requested = new URLSearchParams(window.location.search).get('tab');
  if (['hours', 'formats', 'general', 'team', 'rooms', 'clinic-page', 'extra-services', 'audit'].includes(requested)) {
    return requested;
  }
  if (['#google-calendar', '#telegram-notifications'].includes(window.location.hash)) return 'general';
  return 'hours';
};

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [activeTab, setActiveTab] = useState(settingsTabFromLocation);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formatDialogOpen, setFormatDialogOpen] = useState(false);
  const [editFormat, setEditFormat] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [psychName, setPsychName] = useState('');
  const [overrides, setOverrides] = useState([]);
  const [overrideForm, setOverrideForm] = useState({ date: '', override_type: 'add', start: '09:00', end: '18:00', note: '' });
  const [gcal, setGcal] = useState({ connected: false, email: '', available: false, requires_reconnect: false, last_error: '' });
  const [tg, setTg] = useState({ connected: false, link_url: '', bot_username: '', available: false, new_booking_notifications: true });
  const [profile, setProfile] = useState({ slug: '', bio: '', specialization: '', tags: [], contacts: {}, show_prices: true, avatar: '' });
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ name: '', email: '', password: '' });
  const [adminSaving, setAdminSaving] = useState(false);
  const [teamLimits, setTeamLimits] = useState({ current_admins: 0, max_admins: 0, current_doctors: 0, max_doctors: 1, plan_name: '', is_clinic: false });
  // Doctor management (CLINIC)
  const [doctors, setDoctors] = useState([]);
  const [newDoctor, setNewDoctor] = useState({ name: '', email: '', password: '', specialization: '' });
  const [doctorSaving, setDoctorSaving] = useState(false);
  // Room management
  const [rooms, setRooms] = useState([]);
  const [roomDialog, setRoomDialog] = useState(false);
  const [editRoom, setEditRoom] = useState(null);
  const [roomSaving, setRoomSaving] = useState(false);
  // Auth
  const [userRole, setUserRole] = useState('doctor');
  const [ownerId, setOwnerId] = useState('');
  const [features, setFeatures] = useState([]);
  const [pendingSchedules, setPendingSchedules] = useState([]);
  const navigate = useNavigate();

  const isClinicDoctor = userRole === 'doctor' && !!ownerId;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('gcal');
    if (!result) return;

    if (result === 'success') {
      toast.success('Google Calendar подключён');
    } else {
      const reason = params.get('reason');
      const messages = {
        invalid_state: 'Ссылка подключения истекла. Запустите подключение ещё раз.',
        denied: 'Подключение Google Calendar отменено.',
        token_exchange: 'Google не подтвердил подключение. Попробуйте ещё раз.',
        invalid_client: 'Google отклонил настройки интеграции. Проверьте Client ID и Client Secret.',
        invalid_grant: 'Код авторизации Google истёк или уже использован. Запустите подключение ещё раз.',
        redirect_uri_mismatch: 'Адрес возврата Google Calendar настроен неверно. Обратитесь к администратору.',
        unauthorized_client: 'Для этого OAuth-клиента не разрешено подключение Google Calendar.',
        network: 'Google временно недоступен. Попробуйте ещё раз позже.',
        account: 'Не удалось привязать календарь к этому аккаунту.',
      };
      toast.error(messages[reason] || 'Не удалось подключить Google Calendar');
    }

    params.delete('gcal');
    params.delete('reason');
    const nextQuery = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`);
  }, []);

  useEffect(() => {
    const targetId = window.location.hash.slice(1);
    if (!['google-calendar', 'telegram-notifications'].includes(targetId)) return;
    if (activeTab !== 'general') {
      setActiveTab('general');
      return;
    }
    if (loading) return;
    window.requestAnimationFrame(() => {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [activeTab, loading]);

  useEffect(() => {
    if (!document.cookie.includes('pp_csrf')) { navigate('/login'); return; }
    fetchSettings();
    api.get('/oauth/calendar/status').then(({ data }) => setGcal(data)).catch(() => {});
    api.get('/telegram/status').then(({ data }) => setTg(data)).catch(() => {});
    api.get('/profile').then(({ data }) => {
      if (data && typeof data === 'object') setProfile(p => ({ ...p, ...data }));
    }).catch(() => {});
    api.get('/team/admins').then(({ data }) => setAdmins(Array.isArray(data) ? data : [])).catch(() => {});
    api.get('/team/limits').then(({ data }) => setTeamLimits(data)).catch(() => {});
    api.get('/auth/me').then(({ data }) => {
      setUserRole(data.role || 'doctor');
      setOwnerId(data.owner_id || '');
      setFeatures(data.active_features || []);
      // Load pending schedules for owner/admin
      if (data.role === 'owner' || data.role === 'admin') {
        api.get('/settings/pending-schedules').then(({ data: ps }) => setPendingSchedules(Array.isArray(ps) ? ps : [])).catch(() => {});
      }
    }).catch(() => {});
    // Load doctors if clinic
    api.get('/team/doctors').then(({ data }) => setDoctors(Array.isArray(data) ? data : [])).catch(() => {});
    // Load rooms if feature available
    api.get('/rooms').then(({ data }) => setRooms(Array.isArray(data) ? data : [])).catch(() => {});
  }, [navigate]);

  const fetchSettings = async () => {
    try {
      const [settingsRes, overridesRes] = await Promise.all([
        api.get('/settings'), api.get('/schedule-overrides'),
      ]);
      setSettings(settingsRes.data);
      setPsychName(settingsRes.data.psychologist_name || '');
      setOverrides(overridesRes.data);
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
    }
    setLoading(false);
  };

  const saveWorkingHours = async () => {
    setSaving(true);
    try {
      const resp = await api.put('/settings', { working_hours: settings.working_hours });
      // If doctor submitted pending schedule, update UI
      if (resp.data?.pending_schedule) {
        setSettings(s => ({ ...s, pending_schedule: resp.data.pending_schedule, pending_schedule_at: resp.data.pending_schedule_at }));
        toast.success('Изменения отправлены на одобрение');
      } else {
        toast.success('Рабочие часы сохранены');
      }
    }
    catch { toast.error('Ошибка сохранения'); }
    setSaving(false);
  };

  const saveFormats = async (formats) => {
    try { await api.put('/settings', { meeting_formats: formats }); setSettings(s => ({ ...s, meeting_formats: formats })); toast.success('Форматы сохранены'); }
    catch { toast.error('Ошибка сохранения'); }
  };

  const saveGeneral = async () => {
    setSaving(true);
    const update = { psychologist_name: psychName };
    if (newPassword.trim()) update.new_password = newPassword;
    try { await api.put('/settings', update); toast.success('Настройки сохранены'); setNewPassword(''); }
    catch { toast.error('Ошибка сохранения'); }
    setSaving(false);
  };

  const toggleDay = (day) => setSettings(s => ({ ...s, working_hours: { ...s.working_hours, [day]: { ...s.working_hours[day], enabled: !s.working_hours[day]?.enabled } } }));
  const updateHour = (day, field, value) => setSettings(s => ({ ...s, working_hours: { ...s.working_hours, [day]: { ...s.working_hours[day], [field]: value } } }));

  const addOrUpdateFormat = () => {
    if (!editFormat?.name || !editFormat?.duration) return;
    let formats = [...(settings.meeting_formats || [])];
    if (editFormat.id) { formats = formats.map(f => f.id === editFormat.id ? editFormat : f); }
    else { formats.push({ ...editFormat, id: Math.random().toString(36).slice(2, 10) }); }
    saveFormats(formats);
    setFormatDialogOpen(false); setEditFormat(null);
  };
  const deleteFormat = (id) => saveFormats((settings.meeting_formats || []).filter(f => f.id !== id));

  const addOverride = async () => {
    if (!overrideForm.date) { toast.error('Выберите дату'); return; }
    if (overrideForm.override_type === 'add' && (!overrideForm.start || !overrideForm.end)) { toast.error('Укажите время'); return; }
    try {
      const { data } = await api.post('/schedule-overrides', overrideForm);
      setOverrides(prev => [...prev, data].sort((a, b) => a.date.localeCompare(b.date)));
      setOverrideForm({ date: '', override_type: 'add', start: '09:00', end: '18:00', note: '' });
      toast.success('Исключение добавлено');
    } catch { toast.error('Ошибка'); }
  };
  const deleteOverride = async (id) => {
    try { await api.delete(`/schedule-overrides/${id}`); setOverrides(prev => prev.filter(o => o.id !== id)); toast.success('Удалено'); }
    catch { toast.error('Ошибка'); }
  };

  const isOwner = userRole === 'owner';
  const isClinic = teamLimits.is_clinic;
  const hasRoomFeature = features.includes('room_management');

  if (loading) {
    return (
      <div className="flex min-h-screen" style={{ background: 'var(--app-bg)' }}>
        <Sidebar />
        <main className="flex-1 flex items-center justify-center"><div className="text-stone-500">Загрузка...</div></main>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="flex min-h-screen" style={{ background: 'var(--app-bg)' }}>
        <Sidebar />
        <main className="flex-1 flex items-center justify-center p-6">
          <div className="text-center max-w-sm">
            <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'var(--app-accent-muted)' }}>
              <AlertCircle size={26} style={{ color: 'var(--app-accent)' }} />
            </div>
            <h2 className="font-heading text-lg mb-2" style={{ color: 'var(--app-text)' }}>Не удалось загрузить настройки</h2>
            <p className="text-sm mb-5" style={{ color: 'var(--app-text-muted)' }}>Проверьте подключение к интернету и попробуйте снова.</p>
            <Button onClick={() => { setLoading(true); fetchSettings(); }} className="text-white" style={{ background: 'var(--app-accent)' }}>
              <RefreshCw size={15} className="mr-1.5" /> Повторить
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="flex min-h-screen" style={{ background: 'var(--app-bg)' }}>
      <Sidebar />
      <main className="flex-1 p-4 lg:p-8 pt-16 lg:pt-8 overflow-auto">
        <div className="max-w-4xl mx-auto">
          <h1 className="font-heading text-2xl lg:text-3xl text-stone-200 mb-8 animate-fade-in">Настройки</h1>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="animate-fade-in delay-1">
            <TabsList className="bg-stone-800/50 border border-stone-800 mb-6 flex-wrap h-auto gap-1 p-1">
              <TabsTrigger data-testid="tab-hours" value="hours" className="data-[state=active]:bg-stone-700 data-[state=active]:text-stone-200">
                Рабочие часы
              </TabsTrigger>
              {/* Formats: shown for solo doctors and clinic doctors, hidden for owner */}
              {!isOwner && (
                <TabsTrigger data-testid="tab-formats" value="formats" className="data-[state=active]:bg-stone-700 data-[state=active]:text-stone-200">
                  Форматы встреч
                </TabsTrigger>
              )}
              <TabsTrigger data-testid="tab-general" value="general" className="data-[state=active]:bg-stone-700 data-[state=active]:text-stone-200">
                Общие
              </TabsTrigger>
              {/* Team: hidden for clinic doctors */}
              {!isClinicDoctor && (
                <TabsTrigger data-testid="tab-team" value="team" className="data-[state=active]:bg-stone-700 data-[state=active]:text-stone-200">
                  Команда
                </TabsTrigger>
              )}
              {/* Rooms: hidden for clinic doctors */}
              {hasRoomFeature && !isClinicDoctor && (
                <TabsTrigger data-testid="tab-rooms" value="rooms" className="data-[state=active]:bg-stone-700 data-[state=active]:text-stone-200">
                  Кабинеты
                </TabsTrigger>
              )}
              {isOwner && isClinic && (
                <TabsTrigger data-testid="tab-clinic-page" value="clinic-page" className="data-[state=active]:bg-stone-700 data-[state=active]:text-stone-200">
                  Сайт клиники
                </TabsTrigger>
              )}
              {isOwner && isClinic && (
                <TabsTrigger data-testid="tab-extra-services" value="extra-services" className="data-[state=active]:bg-stone-700 data-[state=active]:text-stone-200">
                  Доп. услуги
                </TabsTrigger>
              )}
              {isOwner && (
                <TabsTrigger data-testid="tab-audit" value="audit" className="data-[state=active]:bg-stone-700 data-[state=active]:text-stone-200">
                  Журнал
                </TabsTrigger>
              )}
            </TabsList>

            {/* ── Working Hours Tab ── */}
            <TabsContent value="hours">
              <WorkingHoursTab
                settings={settings} saving={saving}
                saveWorkingHours={saveWorkingHours} toggleDay={toggleDay} updateHour={updateHour}
                overrides={overrides} overrideForm={overrideForm} setOverrideForm={setOverrideForm}
                addOverride={addOverride} deleteOverride={deleteOverride}
                isClinicDoctor={isClinicDoctor}
                pendingSchedules={pendingSchedules} setPendingSchedules={setPendingSchedules}
                isOwner={isOwner} isClinic={isClinic}
                freeScheduleDistribution={settings?.free_schedule_distribution || false}
              />
            </TabsContent>

            {/* ── Meeting Formats Tab (hidden for Owner) ── */}
            {!isOwner && (
              <TabsContent value="formats">
                <FormatsTab
                  settings={settings}
                  onEdit={(fmt) => { setEditFormat({ ...fmt, currency: fmt.currency || settings?.currency || 'RUB' }); setFormatDialogOpen(true); }}
                  onAdd={() => { setEditFormat({ name: '', duration: 60, description: '', price: 0, currency: settings?.currency || 'RUB' }); setFormatDialogOpen(true); }}
                  onDelete={deleteFormat}
                />
              </TabsContent>
            )}

            {/* ── General Tab ── */}
            <TabsContent value="general">
              <GeneralTab
                psychName={psychName} setPsychName={setPsychName}
                newPassword={newPassword} setNewPassword={setNewPassword}
                saving={saving} saveGeneral={saveGeneral}
                tg={tg} setTg={setTg} gcal={gcal} setGcal={setGcal}
                settings={settings} setSettings={setSettings} features={features} userRole={userRole}
                isClinicDoctor={isClinicDoctor}
              />
            </TabsContent>

            {/* ── Team Tab (hidden for clinic doctors) ── */}
            {!isClinicDoctor && (
              <TabsContent value="team">
              <div className="space-y-6">
                {/* Doctors section — only for owner/CLINIC */}
                {isClinic && isOwner && (
                  <>
                  <DoctorsSection
                    doctors={doctors} setDoctors={setDoctors}
                    newDoctor={newDoctor} setNewDoctor={setNewDoctor}
                    doctorSaving={doctorSaving} setDoctorSaving={setDoctorSaving}
                    teamLimits={teamLimits} setTeamLimits={setTeamLimits}
                  />
                  <ProfileRulesSection />
                  </>
                )}

                {/* Admins section */}
                <AdminsSection
                  admins={admins} setAdmins={setAdmins}
                  newAdmin={newAdmin} setNewAdmin={setNewAdmin}
                  adminSaving={adminSaving} setAdminSaving={setAdminSaving}
                  teamLimits={teamLimits} setTeamLimits={setTeamLimits}
                  isClinic={isClinic} navigate={navigate}
                />
              </div>
            </TabsContent>
            )}

            {/* ── Rooms Tab (hidden for clinic doctors) ── */}
            {hasRoomFeature && !isClinicDoctor && (
              <TabsContent value="rooms">
                <RoomsTab
                  rooms={rooms} setRooms={setRooms}
                  doctors={doctors}
                  isOwner={isOwner}
                  onEdit={(room) => { setEditRoom({ ...room }); setRoomDialog(true); }}
                  onAdd={() => { setEditRoom({ name: '', description: '', assigned_doctor_ids: [], admin_can_manage: false }); setRoomDialog(true); }}
                />
              </TabsContent>
            )}

            {/* ── Clinic Page Tab ── */}
            {isOwner && isClinic && (
              <TabsContent value="clinic-page">
                <ClinicPageTab />
              </TabsContent>
            )}

            {/* ── Extra Services Tab ── */}
            {isOwner && isClinic && (
              <TabsContent value="extra-services">
                <ExtraServicesTab />
              </TabsContent>
            )}
            {isOwner && (
              <TabsContent value="audit">
                <AuditLogTab />
              </TabsContent>
            )}
          </Tabs>
        </div>
      </main>

      {/* Format Dialog */}
      <Dialog open={formatDialogOpen} onOpenChange={setFormatDialogOpen}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200">
          <DialogHeader>
            <DialogTitle className="font-heading text-stone-200">
              {editFormat?.id ? 'Редактировать формат' : 'Новый формат'}
            </DialogTitle>
          </DialogHeader>
          {editFormat && (
            <div className="space-y-4">
              <div>
                <label className="text-stone-400 text-sm font-medium mb-2 block">Название</label>
                <Input data-testid="format-name-input" value={editFormat.name} onChange={e => setEditFormat({ ...editFormat, name: e.target.value })}
                  className="bg-stone-950/50 border-stone-800 text-stone-200" placeholder="Например: Консультация" />
              </div>
              <div>
                <label className="text-stone-400 text-sm font-medium mb-2 block">Длительность (мин.)</label>
                <Input data-testid="format-duration-input" type="number" value={editFormat.duration} onChange={e => setEditFormat({ ...editFormat, duration: parseInt(e.target.value) || 0 })}
                  className="bg-stone-950/50 border-stone-800 text-stone-200" />
              </div>
              <div>
                <label className="text-stone-400 text-sm font-medium mb-2 block">
                  Стоимость ({currencySymbol(editFormat.currency || settings?.currency)})
                </label>
                <Input data-testid="format-price-input" type="number" value={editFormat.price || 0} onChange={e => setEditFormat({ ...editFormat, price: parseInt(e.target.value) || 0 })}
                  className="bg-stone-950/50 border-stone-800 text-stone-200" placeholder="0 — бесплатно" />
              </div>
              <div>
                <label className="text-stone-400 text-sm font-medium mb-2 block">Валюта стоимости</label>
                <Select
                  value={editFormat.currency || settings?.currency || 'RUB'}
                  onValueChange={currency => setEditFormat({ ...editFormat, currency })}
                >
                  <SelectTrigger data-testid="format-currency-select" className="bg-stone-950/50 border-stone-800 text-stone-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-800">
                    {CURRENCY_OPTIONS.map(({ code, symbol, label }) => (
                      <SelectItem key={code} value={code}>{symbol} {code} — {label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-stone-600 text-xs mt-1.5">Валюта относится только к этому формату. Сумма автоматически не пересчитывается.</p>
              </div>
              <div>
                <label className="text-stone-400 text-sm font-medium mb-2 block">Описание</label>
                <Input data-testid="format-description-input" value={editFormat.description || ''} onChange={e => setEditFormat({ ...editFormat, description: e.target.value })}
                  className="bg-stone-950/50 border-stone-800 text-stone-200" placeholder="Краткое описание" />
              </div>
              {/* Group session settings */}
              <div className="pt-3 border-t border-stone-800/50 space-y-3">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="text-stone-300 text-sm">Групповой формат</p>
                    <p className="text-stone-600 text-xs">Несколько участников в одном сеансе</p>
                  </div>
                  <Switch checked={editFormat.is_group || false} onCheckedChange={v => setEditFormat({ ...editFormat, is_group: v, max_participants: v ? (editFormat.max_participants || 6) : 1 })}
                    className="data-[state=checked]:bg-amber-700" />
                </label>
                {editFormat.is_group && (
                  <div className="space-y-3">
                    <div>
                      <label className="text-stone-400 text-xs mb-1 block">Макс. участников</label>
                      <Input type="number" min="2" max="50" value={editFormat.max_participants || 6}
                        onChange={e => setEditFormat({ ...editFormat, max_participants: parseInt(e.target.value) || 6 })}
                        className="bg-stone-950/50 border-stone-800 text-stone-200 w-24" />
                    </div>
                    <div>
                      <label className="text-stone-400 text-xs mb-2 block">Тип цены</label>
                      <div className="flex gap-2">
                        <button onClick={() => setEditFormat({ ...editFormat, group_price_type: 'per_person' })}
                          className={`flex-1 px-3 py-2 rounded-lg text-xs border transition-all ${
                            (editFormat.group_price_type || 'per_person') === 'per_person'
                              ? 'bg-amber-700/15 border-amber-700/50 text-amber-400' : 'bg-stone-950/50 border-stone-800 text-stone-500'
                          }`}>
                          С человека
                          <p className="text-[10px] mt-0.5 opacity-70">{formatMoney(editFormat.price || 0, editFormat.currency || settings?.currency)} × кол-во</p>
                        </button>
                        <button onClick={() => setEditFormat({ ...editFormat, group_price_type: 'total' })}
                          className={`flex-1 px-3 py-2 rounded-lg text-xs border transition-all ${
                            editFormat.group_price_type === 'total'
                              ? 'bg-amber-700/15 border-amber-700/50 text-amber-400' : 'bg-stone-950/50 border-stone-800 text-stone-500'
                          }`}>
                          Общая
                          <p className="text-[10px] mt-0.5 opacity-70">{formatMoney(editFormat.price || 0, editFormat.currency || settings?.currency)} за всех</p>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setFormatDialogOpen(false)} className="bg-stone-800 text-stone-300 hover:bg-stone-700">Отмена</Button>
            <Button data-testid="save-format-btn" onClick={addOrUpdateFormat} className="bg-amber-700 hover:bg-amber-600 text-white">Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Room Dialog */}
      <Dialog open={roomDialog} onOpenChange={setRoomDialog}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200">
          <DialogHeader>
            <DialogTitle className="font-heading text-stone-200">
              {editRoom?.id ? 'Редактировать кабинет' : 'Новый кабинет'}
            </DialogTitle>
          </DialogHeader>
          {editRoom && (
            <div className="space-y-4">
              <div>
                <label className="text-stone-400 text-sm font-medium mb-2 block">Название</label>
                <Input data-testid="room-name-input" value={editRoom.name} onChange={e => setEditRoom({ ...editRoom, name: e.target.value })}
                  className="bg-stone-950/50 border-stone-800 text-stone-200" placeholder="Кабинет 1" />
              </div>
              <div>
                <label className="text-stone-400 text-sm font-medium mb-2 block">Описание</label>
                <Input data-testid="room-description-input" value={editRoom.description || ''} onChange={e => setEditRoom({ ...editRoom, description: e.target.value })}
                  className="bg-stone-950/50 border-stone-800 text-stone-200" placeholder="Этаж, оборудование и т.д." />
              </div>
              {doctors.length > 0 && (
                <div>
                  <label className="text-stone-400 text-sm font-medium mb-2 block">Назначенные врачи</label>
                  <div className="space-y-2">
                    {doctors.map(doc => (
                      <label key={doc.id} className="flex items-center gap-2 cursor-pointer text-sm text-stone-400 p-2 rounded-lg bg-stone-950/30 border border-stone-800/50 hover:border-stone-700 transition-colors">
                        <input data-testid={`room-doctor-${doc.id}`} type="checkbox"
                          checked={(editRoom.assigned_doctor_ids || []).includes(doc.id)}
                          onChange={e => {
                            const ids = [...(editRoom.assigned_doctor_ids || [])];
                            if (e.target.checked) ids.push(doc.id);
                            else ids.splice(ids.indexOf(doc.id), 1);
                            setEditRoom({ ...editRoom, assigned_doctor_ids: ids });
                          }}
                          className="w-4 h-4 rounded border-stone-700 bg-stone-800 accent-amber-500" />
                        <span className="text-stone-200">{doc.name}</span>
                        {doc.specialization && <span className="text-stone-600 text-xs">({doc.specialization})</span>}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {isOwner && (
                <div>
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-stone-400 p-3 rounded-lg bg-stone-950/30 border border-stone-800/50">
                    <input data-testid="room-admin-manage" type="checkbox"
                      checked={editRoom.admin_can_manage || false}
                      onChange={e => setEditRoom({ ...editRoom, admin_can_manage: e.target.checked })}
                      className="w-4 h-4 rounded border-stone-700 bg-stone-800 accent-amber-500" />
                    <span className="text-stone-200">Администраторы могут управлять этим кабинетом</span>
                  </label>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRoomDialog(false)} className="bg-stone-800 text-stone-300 hover:bg-stone-700">Отмена</Button>
            <Button data-testid="save-room-btn" disabled={roomSaving || !editRoom?.name}
              className="bg-amber-700 hover:bg-amber-600 text-white"
              onClick={async () => {
                setRoomSaving(true);
                try {
                  if (editRoom.id) {
                    const { data } = await api.put(`/rooms/${editRoom.id}`, editRoom);
                    setRooms(prev => prev.map(r => r.id === data.id ? data : r));
                    toast.success('Кабинет обновлён');
                  } else {
                    const { data } = await api.post('/rooms', editRoom);
                    setRooms(prev => [...prev, data]);
                    toast.success('Кабинет создан');
                  }
                  setRoomDialog(false); setEditRoom(null);
                } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
                setRoomSaving(false);
              }}>
              {roomSaving ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ── Working Hours Tab ── */
function WorkingHoursTab({ settings, saving, saveWorkingHours, toggleDay, updateHour, overrides, overrideForm, setOverrideForm, addOverride, deleteOverride, isClinicDoctor, pendingSchedules, setPendingSchedules, isOwner, isClinic, freeScheduleDistribution }) {
  const hasPendingSchedule = isClinicDoctor && !!settings?.pending_schedule;
  const needsApproval = isClinicDoctor && !freeScheduleDistribution;

  const approveSchedule = async (doctorId) => {
    try {
      await api.put(`/settings/approve-schedule/${doctorId}`);
      setPendingSchedules(prev => prev.filter(p => p.doctor_id !== doctorId));
      toast.success('Расписание одобрено');
    } catch { toast.error('Ошибка'); }
  };
  const rejectSchedule = async (doctorId) => {
    try {
      await api.put(`/settings/reject-schedule/${doctorId}`);
      setPendingSchedules(prev => prev.filter(p => p.doctor_id !== doctorId));
      toast.success('Изменения отклонены');
    } catch { toast.error('Ошибка'); }
  };

  const toggleFreeDistribution = async (value) => {
    try {
      await api.put('/settings', { free_schedule_distribution: value });
      toast.success(value ? 'Свободное распределение включено' : 'Свободное распределение выключено');
    } catch { toast.error('Ошибка'); }
  };

  return (
    <>
      {/* Pending schedule banner for clinic doctor */}
      {hasPendingSchedule && (
        <Card className="bg-amber-900/15 border-amber-800/40 mb-4">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0" />
            <div className="flex-1">
              <p className="text-amber-400 text-sm font-medium">Изменения ожидают одобрения администрации</p>
              <p className="text-stone-500 text-xs mt-0.5">Текущее расписание отображается ниже. Новые часы будут применены после подтверждения.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Free schedule distribution toggle (Owner only) */}
      {isOwner && isClinic && (
        <Card className="bg-stone-900 border-stone-800 mb-4">
          <CardContent className="p-4">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <p className="text-stone-200 text-sm font-medium">Свободное распределение времени специалистами</p>
                <p className="text-stone-500 text-xs mt-0.5">Врачи смогут менять свои рабочие часы без одобрения администрации</p>
              </div>
              <Switch data-testid="free-schedule-toggle" checked={freeScheduleDistribution} onCheckedChange={toggleFreeDistribution} className="data-[state=checked]:bg-amber-700" />
            </label>
          </CardContent>
        </Card>
      )}

      {/* Pending schedule requests (Owner/Admin) */}
      {(isOwner || (!isClinicDoctor && pendingSchedules.length > 0)) && pendingSchedules.length > 0 && (
        <Card className="bg-stone-900 border-amber-900/30 mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-stone-200 font-heading text-lg flex items-center gap-2">
              Заявки на изменение расписания
              <span className="text-xs bg-amber-700/30 text-amber-400 px-2 py-0.5 rounded-full">{pendingSchedules.length}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingSchedules.map(ps => (
              <div key={ps.doctor_id} data-testid={`pending-schedule-${ps.doctor_id}`} className="p-4 rounded-lg bg-stone-950/40 border border-stone-800">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-stone-200 text-sm font-medium">{ps.doctor_name}</p>
                    <p className="text-stone-600 text-xs">Запрос от {ps.pending_schedule_at ? new Date(ps.pending_schedule_at).toLocaleDateString('ru-RU') : '—'}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid={`approve-schedule-${ps.doctor_id}`} size="sm" onClick={() => approveSchedule(ps.doctor_id)} className="bg-green-800 hover:bg-green-700 text-white text-xs">
                      Одобрить
                    </Button>
                    <Button data-testid={`reject-schedule-${ps.doctor_id}`} size="sm" variant="destructive" onClick={() => rejectSchedule(ps.doctor_id)} className="bg-red-900/50 hover:bg-red-800 text-red-300 text-xs">
                      Отклонить
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {DAYS_ORDER.map(day => {
                    const cur = (ps.current_schedule || {})[day];
                    const pend = (ps.pending_schedule || {})[day];
                    const changed = JSON.stringify(cur) !== JSON.stringify(pend);
                    if (!pend?.enabled && !cur?.enabled) return null;
                    return (
                      <div key={day} className={`p-2 rounded border ${changed ? 'border-amber-800/50 bg-amber-900/10' : 'border-stone-800/50 bg-stone-950/30'}`}>
                        <span className="text-stone-400">{DAY_LABELS[day]}:</span>
                        {pend?.enabled ? (
                          <span className={`ml-1 ${changed ? 'text-amber-400' : 'text-stone-300'}`}>{pend.start}–{pend.end}</span>
                        ) : (
                          <span className="ml-1 text-stone-600">выходной</span>
                        )}
                        {changed && cur?.enabled && (
                          <span className="text-stone-600 ml-1">(было: {cur.start}–{cur.end})</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className="bg-stone-900 border-stone-800">
        <CardHeader><CardTitle className="text-stone-200 font-heading text-lg">Рабочие часы</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {DAYS_ORDER.map(day => {
            const hours = settings.working_hours?.[day] || { enabled: false, start: '09:00', end: '18:00' };
            return (
              <div key={day} data-testid={`day-${day}`} className="flex items-center gap-4 p-3 rounded-lg bg-stone-950/30 border border-stone-800/50">
                <Switch data-testid={`switch-${day}`} checked={hours.enabled} onCheckedChange={() => toggleDay(day)} className="data-[state=checked]:bg-amber-700" />
                <span className={`w-28 text-sm ${hours.enabled ? 'text-stone-200' : 'text-stone-600'}`}>{DAY_LABELS[day]}</span>
                {hours.enabled && (
                  <div className="flex items-center gap-2">
                    <Input data-testid={`start-${day}`} type="time" value={hours.start} onChange={e => updateHour(day, 'start', e.target.value)}
                      className="w-28 bg-stone-950/50 border-stone-800 text-stone-200 text-sm" />
                    <span className="text-stone-600">-</span>
                    <Input data-testid={`end-${day}`} type="time" value={hours.end} onChange={e => updateHour(day, 'end', e.target.value)}
                      className="w-28 bg-stone-950/50 border-stone-800 text-stone-200 text-sm" />
                  </div>
                )}
              </div>
            );
          })}
          <Button data-testid="save-hours-btn" onClick={saveWorkingHours} disabled={saving} className={`text-white mt-4 ${needsApproval ? 'bg-sky-700 hover:bg-sky-600' : 'bg-amber-700 hover:bg-amber-600'}`}>
            <Save size={16} className="mr-2" />{saving ? 'Сохранение...' : needsApproval ? 'Отправить на одобрение' : 'Сохранить'}
          </Button>
        </CardContent>
      </Card>

      {/* Cyclic Schedule (Advanced) */}
      <CyclicScheduleSection settings={settings} />

      {/* Workload limits */}
      <WorkloadLimitsCard settings={settings} />

      {/* Schedule Overrides */}
      <Card className="bg-stone-900 border-stone-800 mt-6">
        <CardHeader>
          <CardTitle className="text-stone-200 font-heading text-lg">Исключения и дополнения</CardTitle>
          <p className="text-stone-500 text-xs mt-1">Добавьте рабочие часы вне шаблона или отмените рабочий день</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3 p-4 rounded-lg bg-stone-950/30 border border-stone-800/50">
            <div>
              <label className="text-stone-500 text-xs mb-1.5 block">Дата</label>
              <Input data-testid="override-date" type="date" value={overrideForm.date} onChange={e => setOverrideForm({ ...overrideForm, date: e.target.value })}
                className="w-40 bg-stone-950/50 border-stone-800 text-stone-200 text-sm" />
            </div>
            <div>
              <label className="text-stone-500 text-xs mb-1.5 block">Тип</label>
              <Select value={overrideForm.override_type} onValueChange={v => setOverrideForm({ ...overrideForm, override_type: v })}>
                <SelectTrigger data-testid="override-type" className="w-44 bg-stone-950/50 border-stone-800 text-stone-300 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-stone-900 border-stone-800">
                  <SelectItem value="add">Добавить часы</SelectItem>
                  <SelectItem value="remove">Выходной</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {overrideForm.override_type === 'add' && (
              <>
                <div>
                  <label className="text-stone-500 text-xs mb-1.5 block">С</label>
                  <Input data-testid="override-start" type="time" value={overrideForm.start} onChange={e => setOverrideForm({ ...overrideForm, start: e.target.value })}
                    className="w-28 bg-stone-950/50 border-stone-800 text-stone-200 text-sm" />
                </div>
                <div>
                  <label className="text-stone-500 text-xs mb-1.5 block">До</label>
                  <Input data-testid="override-end" type="time" value={overrideForm.end} onChange={e => setOverrideForm({ ...overrideForm, end: e.target.value })}
                    className="w-28 bg-stone-950/50 border-stone-800 text-stone-200 text-sm" />
                </div>
              </>
            )}
            <div>
              <label className="text-stone-500 text-xs mb-1.5 block">Заметка</label>
              <Input data-testid="override-note" value={overrideForm.note} onChange={e => setOverrideForm({ ...overrideForm, note: e.target.value })}
                placeholder="Причина" className="w-32 bg-stone-950/50 border-stone-800 text-stone-200 text-sm placeholder:text-stone-600" />
            </div>
            <Button data-testid="add-override-btn" size="sm" onClick={addOverride} className="bg-amber-700 hover:bg-amber-600 text-white">
              <Plus size={14} className="mr-1" /> Добавить
            </Button>
          </div>
          {overrides.length === 0 ? (
            <p className="text-stone-600 text-sm text-center py-3">Нет исключений</p>
          ) : (
            <div className="space-y-2">
              {overrides.map(o => (
                <div key={o.id} data-testid={`override-${o.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-stone-950/30 border border-stone-800/50 hover:border-stone-700 transition-colors">
                  {o.override_type === 'add' ? <CalendarPlus size={16} className="text-green-500 shrink-0" /> : <CalendarMinus size={16} className="text-red-400 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-stone-200 text-sm font-medium">
                        {(() => { try { return format(new Date(o.date + 'T00:00'), 'd MMMM yyyy, EEEE', { locale: ru }); } catch { return o.date; } })()}
                      </span>
                      <Badge className={`text-[10px] ${o.override_type === 'add' ? 'bg-green-900/30 text-green-400 border border-green-900/50' : 'bg-red-900/30 text-red-400 border border-red-900/50'}`}>
                        {o.override_type === 'add' ? `${o.start} - ${o.end}` : 'Выходной'}
                      </Badge>
                    </div>
                    {o.note && <p className="text-stone-600 text-xs mt-0.5">{o.note}</p>}
                  </div>
                  <Button data-testid={`delete-override-${o.id}`} variant="ghost" size="sm" onClick={() => deleteOverride(o.id)}
                    className="text-stone-600 hover:text-red-400 h-7 w-7 p-0 shrink-0"><Trash2 size={14} /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}


/* ── Meeting Formats Tab ── */
function FormatsTab({ settings, onEdit, onAdd, onDelete }) {
  return (
    <Card className="bg-stone-900 border-stone-800">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-stone-200 font-heading text-lg">Форматы встреч</CardTitle>
        <Button data-testid="add-format-btn" size="sm" onClick={onAdd} className="bg-amber-700 hover:bg-amber-600 text-white">
          <Plus size={16} className="mr-1" /> Добавить
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {(settings.meeting_formats || []).map(fmt => (
          <div key={fmt.id} data-testid={`format-${fmt.id}`}
            className="flex items-center gap-4 p-4 rounded-lg bg-stone-950/30 border border-stone-800/50 hover:border-stone-700 transition-colors">
            <div className="flex-1">
              <p className="text-stone-200 font-medium text-sm">{fmt.name}</p>
              <p className="text-stone-500 text-xs mt-0.5">
                {fmt.duration} мин. / {fmt.price ? formatMoney(fmt.price, fmt.currency || settings.currency) : 'Бесплатно'} {fmt.description && `/ ${fmt.description}`}
                {fmt.is_group && <span className="ml-1 text-violet-400">· групповой (до {fmt.max_participants || 6}, {fmt.group_price_type === 'total' ? 'общая цена' : 'с человека'})</span>}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onEdit(fmt)} className="text-stone-500 hover:text-amber-500">Изменить</Button>
            <Button data-testid={`delete-format-${fmt.id}`} variant="ghost" size="sm" onClick={() => onDelete(fmt.id)}
              className="text-stone-500 hover:text-red-400 h-8 w-8 p-0"><Trash2 size={16} /></Button>
          </div>
        ))}
        {(settings.meeting_formats || []).length === 0 && <p className="text-stone-600 text-center py-4 text-sm">Нет форматов встреч</p>}
      </CardContent>
    </Card>
  );
}


/* ── General Tab ── */
function GeneralTab({ psychName, setPsychName, newPassword, setNewPassword, saving, saveGeneral, tg, setTg, gcal, setGcal, settings, setSettings, features, userRole, isClinicDoctor }) {
  const connectGoogle = async () => {
    try {
      const { data } = await api.get('/oauth/calendar/connect');
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Не удалось начать подключение Google Calendar');
    }
  };

  const hasSecureChat = features.includes('secure_chat');
  const [chatDelegation, setChatDelegation] = useState(settings?.chat_delegation || false);
  const [confirmPw, setConfirmPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [telegramBusy, setTelegramBusy] = useState('');

  const refreshTelegramStatus = useCallback(async (issueLink = false) => {
    try {
      const { data } = await api.get('/telegram/status', { params: { issue_link: issueLink } });
      setTg(current => data.connected ? data : {
        ...current,
        ...data,
        link_url: data.link_url || current.link_url,
      });
      return data;
    } catch {
      return null;
    }
  }, [setTg]);

  useEffect(() => {
    const checkAfterTelegram = async () => {
      if (tg.connected) return;
      const result = await refreshTelegramStatus(false);
      if (result?.connected) toast.success('Telegram подключён, уведомления активны');
    };
    window.addEventListener('focus', checkAfterTelegram);
    return () => window.removeEventListener('focus', checkAfterTelegram);
  }, [tg.connected, refreshTelegramStatus]);

  const updateTelegramPreference = async (enabled) => {
    setTelegramBusy('preference');
    try {
      const { data } = await api.put('/telegram/preferences', null, { params: { new_booking: enabled } });
      setTg(current => ({ ...current, ...data }));
      toast.success(enabled ? 'Уведомления о новых записях включены' : 'Уведомления о новых записях выключены');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Не удалось изменить уведомления');
    }
    setTelegramBusy('');
  };

  const testTelegram = async () => {
    setTelegramBusy('test');
    try {
      await api.post('/telegram/test');
      toast.success('Тестовое сообщение отправлено в Telegram');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Не удалось отправить тестовое сообщение');
    }
    setTelegramBusy('');
  };

  const disconnectTelegram = async () => {
    setTelegramBusy('disconnect');
    try {
      await api.delete('/telegram/disconnect');
      const fresh = await refreshTelegramStatus(true);
      setTg(current => ({ ...current, ...(fresh || {}), connected: false }));
      toast.success('Telegram отключён');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Не удалось отключить Telegram');
    }
    setTelegramBusy('');
  };

  // Name auto-saves on blur, like every other setting on this page.
  const saveName = async () => {
    const name = psychName.trim();
    if (!name || name === (settings?.psychologist_name || '')) return;
    try { await api.put('/settings', { psychologist_name: name }); toast.success('Имя сохранено'); }
    catch { toast.error('Не удалось сохранить имя'); }
  };

  // Password is the one explicit action: changing it shouldn't happen on a stray blur.
  const changePassword = async () => {
    if (newPassword.length < 6) { toast.error('Пароль минимум 6 символов'); return; }
    if (newPassword !== confirmPw) { toast.error('Пароли не совпадают'); return; }
    setPwSaving(true);
    try { await api.put('/settings', { new_password: newPassword }); toast.success('Пароль изменён'); setNewPassword(''); setConfirmPw(''); }
    catch { toast.error('Не удалось изменить пароль'); }
    setPwSaving(false);
  };

  const cardCls = 'rounded-xl border p-5 sm:p-6';
  const cardStyle = { background: 'var(--app-card)', borderColor: 'var(--app-border)' };
  const titleCls = 'font-heading text-base mb-4';

  return (
    <div className="space-y-5">
      {/* Account + Appearance side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* ── Аккаунт ── */}
        <div className={cardCls} style={cardStyle}>
          <h3 className={titleCls} style={{ color: 'var(--app-text)' }}>Аккаунт</h3>
          <div className="space-y-5">
            <div>
              <label className="text-stone-400 text-sm font-medium mb-1.5 block">Имя</label>
              <Input data-testid="psychologist-name-input" value={psychName}
                onChange={e => setPsychName(e.target.value)} onBlur={saveName}
                className="bg-stone-950/50 border-stone-800 text-stone-200" />
              <p className="text-stone-500 text-xs mt-1.5">Сохраняется автоматически.</p>
            </div>
            <div className="pt-5 border-t border-stone-800">
              <label className="text-stone-400 text-sm font-medium mb-2 block">Сменить пароль</label>
              <div className="space-y-2.5">
                <Input data-testid="new-password-input" type="password" value={newPassword} autoComplete="new-password"
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Новый пароль (минимум 6 символов)" className="bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-600" />
                <Input data-testid="confirm-password-input" type="password" value={confirmPw} autoComplete="new-password"
                  onChange={e => setConfirmPw(e.target.value)}
                  placeholder="Повторите новый пароль" className="bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-600" />
                <Button data-testid="change-password-btn" onClick={changePassword} disabled={pwSaving || !newPassword}
                  className="text-white" style={{ background: 'var(--app-accent)' }}>
                  {pwSaving ? 'Сохранение...' : 'Сменить пароль'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Внешний вид ── */}
        <div className={cardCls} style={cardStyle}>
          <h3 className={titleCls} style={{ color: 'var(--app-text)' }}>Внешний вид</h3>
          <div className="space-y-5">
            <ThemeSelector />
            <div className="pt-5 border-t border-stone-800">
              <label className="text-stone-400 text-sm font-medium mb-2 block">Валюта цен</label>
              <Select
                value={settings?.currency || 'RUB'}
                disabled={isClinicDoctor}
                onValueChange={async (value) => {
                  try {
                    await api.put('/settings', { currency: value });
                    setSettings(current => ({ ...current, currency: value }));
                    toast.success('Валюта сохранена');
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Не удалось сохранить валюту');
                  }
                }}
              >
                <SelectTrigger data-testid="currency-select" className="bg-stone-950/50 border-stone-800 text-stone-200 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCY_OPTIONS.map(({ code, label, symbol }) => (
                    <SelectItem key={code} value={code}>{label} ({symbol})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-stone-500 text-xs mt-1.5">
                {isClinicDoctor
                  ? 'Валюта задаётся в настройках организации.'
                  : 'Меняет обозначение цен, балансов и финансов. Суммы не пересчитываются.'}
              </p>
            </div>
            <div className="pt-5 border-t border-stone-800">
              <label className="text-stone-400 text-sm font-medium mb-2 block">Часовой пояс</label>
              <Select
                value={settings?.timezone || DEFAULT_TIMEZONE}
                onValueChange={async (value) => {
                  try {
                    await api.put('/settings', { timezone: value });
                    setSettings(current => ({ ...current, timezone: value }));
                    toast.success('Часовой пояс сохранён');
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Не удалось сохранить часовой пояс');
                  }
                }}
              >
                <SelectTrigger data-testid="timezone-select" className="bg-stone-950/50 border-stone-800 text-stone-200 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONE_OPTIONS.map(({ value, city }) => (
                    <SelectItem key={value} value={value}>{city} · {timezoneLabel(value).split(', ').slice(1).join(', ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-stone-500 text-xs mt-1.5">
                По этому времени работают расписание, напоминания и Google Calendar.
              </p>
            </div>
            {!isClinicDoctor && (
              <div className="pt-5 border-t border-stone-800">
                <label className="text-stone-400 text-sm font-medium mb-2 block">Тип деятельности</label>
                <Select value={settings?.industry_type || 'GENERAL'}
                  onValueChange={async (v) => {
                    try {
                      await api.put('/settings', { industry_type: v });
                      toast.success('Тип деятельности обновлён. Обновите страницу для применения терминологии.');
                    } catch { toast.error('Ошибка'); }
                  }}>
                  <SelectTrigger data-testid="industry-type-select" className="bg-stone-950/50 border-stone-800 text-stone-200 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GENERAL">Общий профиль</SelectItem>
                    <SelectItem value="MEDICAL">Медицина / Психология</SelectItem>
                    <SelectItem value="BEAUTY">Красота / Массаж / SPA</SelectItem>
                    <SelectItem value="EDUCATION">Образование / Репетиторство</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-stone-500 text-xs mt-1.5">Влияет на терминологию в интерфейсе (пациенты/клиенты/ученики).</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Уведомления и доступ (hidden for clinic doctors) ── */}
      {!isClinicDoctor && (
        <div className={cardCls} style={cardStyle}>
          <h3 className={titleCls} style={{ color: 'var(--app-text)' }}>Уведомления и доступ</h3>
          <div className="space-y-4">
            <label className="flex items-center justify-between cursor-pointer p-3 rounded-lg bg-stone-950/50 border border-stone-800">
              <div className="pr-3">
                <p className="text-stone-200 text-sm">Напоминания о записи</p>
                <p className="text-stone-500 text-xs mt-0.5">Клиент получит уведомление в Telegram/SMS с кнопками подтвердить/отменить/перенести.</p>
              </div>
              <Switch data-testid="patient-notif-toggle" checked={settings?.patient_notifications_enabled || false} onCheckedChange={async v => {
                try { await api.put('/settings', { patient_notifications_enabled: v }); toast.success(v ? 'Уведомления включены' : 'Уведомления выключены'); } catch { toast.error('Ошибка'); }
              }} />
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-stone-400 text-xs mb-1 block">Напоминания до записи (часы)</label>
                <Input data-testid="reminder-hours" type="text" inputMode="decimal"
                  defaultValue={(settings?.reminder_schedule_hours || [settings?.reminder_hours_before ?? 24, 2]).join(', ')}
                  placeholder="24, 2"
                  className="bg-stone-950/50 border-stone-800 text-stone-200 text-sm"
                  onBlur={async e => {
                    const values = e.target.value.split(/[;,\s]+/).map(Number).filter(v => Number.isFinite(v) && v >= 0.25 && v <= 168);
                    if (!values.length || values.length > 5) { toast.error('Укажите от 1 до 5 значений, например: 24, 2'); return; }
                    try { await api.put('/settings', { reminder_schedule_hours: values }); toast.success('Расписание напоминаний сохранено'); } catch { toast.error('Ошибка'); }
                  }} />
                <p className="text-stone-500 text-xs mt-1">Например: 24, 2</p>
              </div>
              <div>
                <label className="text-stone-400 text-xs mb-1 block">Автоотмена за (часов)</label>
                <Input data-testid="auto-cancel-hours" type="number" step="0.5" min="0"
                  defaultValue={settings?.auto_cancel_hours_before ?? 0}
                  className="bg-stone-950/50 border-stone-800 text-stone-200 text-sm"
                  onBlur={async e => {
                    const v = parseFloat(e.target.value) || 0;
                    try { await api.put('/settings', { auto_cancel_hours_before: v }); toast.success('Сохранено'); } catch { toast.error('Ошибка'); }
                  }} />
                <p className="text-stone-500 text-xs mt-1">0 = не отменять</p>
              </div>
              <div>
                <label className="text-stone-400 text-xs mb-1 block">Мин. время до записи (часов)</label>
                <Input type="number" min="0" max="168" step="0.5"
                  defaultValue={settings?.min_booking_lead_hours ?? 0}
                  onBlur={async e => {
                    const v = parseFloat(e.target.value) || 0;
                    try { await api.put('/settings', { min_booking_lead_hours: v }); toast.success('Сохранено'); } catch { toast.error('Ошибка'); }
                  }} className="bg-stone-950/50 border-stone-800 text-stone-200 text-sm" />
                <p className="text-stone-500 text-xs mt-1">0 = без ограничений</p>
              </div>
            </div>

            {/* OTP verification */}
            {hasSecureChat && (userRole === 'doctor' || userRole === 'owner') && (
              <label className="flex items-center justify-between cursor-pointer p-3 rounded-lg bg-stone-950/50 border border-stone-800">
                <div className="pr-3">
                  <p className="text-stone-200 text-sm">OTP-верификация клиентов</p>
                  <p className="text-stone-500 text-xs mt-0.5">Клиент подтверждает личность кодом из email перед доступом к чату и записи.</p>
                </div>
                <Switch data-testid="otp-toggle" checked={settings?.require_patient_otp !== false} onCheckedChange={async v => {
                  try { await api.put('/settings', { require_patient_otp: v }); toast.success(v ? 'OTP включён' : 'OTP отключён'); } catch { toast.error('Ошибка'); }
                }} />
              </label>
            )}

            {/* Chat delegation + grace period */}
            {hasSecureChat && (userRole === 'doctor' || userRole === 'owner') && (
              <div className="space-y-3">
                <label className="flex items-center justify-between cursor-pointer p-3 rounded-lg bg-stone-950/50 border border-stone-800">
                  <div className="pr-3">
                    <p className="text-stone-200 text-sm">Делегировать чаты администраторам</p>
                    <p className="text-stone-500 text-xs mt-0.5">Новые чаты сначала попадают к администраторам. Вы увидите их только после эскалации.</p>
                  </div>
                  <Switch data-testid="chat-delegation-toggle" checked={chatDelegation} onCheckedChange={async v => {
                    try { await api.put('/settings', { chat_delegation: v }); setChatDelegation(v); toast.success(v ? 'Делегирование включено' : 'Делегирование выключено'); }
                    catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
                  }} />
                </label>
                <div className="flex items-end gap-3">
                  <div>
                    <label className="text-stone-400 text-xs mb-1 block">Грейс-период (дней)</label>
                    <Input data-testid="grace-period-input" type="number" min="0" max="90"
                      defaultValue={settings?.chat_grace_period_days ?? 7}
                      className="w-24 bg-stone-950/50 border-stone-800 text-stone-200 text-sm"
                      onBlur={async e => {
                        const v = parseInt(e.target.value) || 0;
                        try { await api.put('/settings', { chat_grace_period_days: v }); toast.success('Сохранено'); } catch { toast.error('Ошибка'); }
                      }} />
                  </div>
                  <p className="text-stone-500 text-xs pb-2">После этого срока чат перенаправляется к администратору.</p>
                </div>
              </div>
            )}

            <p className="text-stone-500 text-xs">
              Групповые записи настраиваются отдельно для каждой услуги: в разделе «Форматы встреч» отметьте формат как групповой и задайте число участников.
            </p>
          </div>
        </div>
      )}

      {/* ── Интеграции ── */}
      <div className={cardCls} style={cardStyle}>
        <h3 className={titleCls} style={{ color: 'var(--app-text)' }}>Интеграции</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {/* Telegram */}
          <div id="telegram-notifications" className="scroll-mt-6">
            <div className="flex items-center gap-2 mb-1.5">
              <BellRing size={16} style={{ color: 'var(--app-accent)' }} aria-hidden="true" />
              <h4 className="text-sm font-medium" style={{ color: 'var(--app-text)' }}>Новые записи в Telegram</h4>
            </div>
            <p className="mb-3 text-xs leading-relaxed" style={{ color: 'var(--app-text-muted)' }}>
              Получайте новую заявку сразу после записи через визитку или ссылку. В сообщении можно подтвердить или отклонить запись.
            </p>
            {!tg.available ? (
              <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>Telegram-бот временно недоступен.</p>
            ) : tg.connected ? (
              <div className="rounded-lg border p-3 space-y-3" style={{ borderColor: 'var(--app-border)', background: 'var(--app-bg)' }}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-emerald-500">Telegram подключён</p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--app-text-muted)' }}>
                      {tg.new_booking_notifications ? 'Новые заявки будут приходить сразу.' : 'Уведомления о новых заявках выключены.'}
                    </p>
                  </div>
                  <Button size="sm" variant="ghost" disabled={!!telegramBusy} className="text-red-400 hover:text-red-300"
                    onClick={disconnectTelegram}>Отключить</Button>
                </div>
                <label className="flex items-center justify-between gap-3 cursor-pointer pt-3 border-t" style={{ borderColor: 'var(--app-border)' }}>
                  <span className="text-sm" style={{ color: 'var(--app-text)' }}>Сообщать о новых записях</span>
                  <Switch
                    data-testid="telegram-new-booking-toggle"
                    checked={tg.new_booking_notifications !== false}
                    disabled={telegramBusy === 'preference'}
                    onCheckedChange={updateTelegramPreference}
                  />
                </label>
                <Button data-testid="telegram-test-btn" size="sm" variant="outline" onClick={testTelegram}
                  disabled={!!telegramBusy} className="w-full" style={{ borderColor: 'var(--app-border)', color: 'var(--app-text)' }}>
                  <Send size={14} className="mr-1.5" aria-hidden="true" />
                  {telegramBusy === 'test' ? 'Отправляем...' : 'Отправить тестовое сообщение'}
                </Button>
              </div>
            ) : (
              <div className="rounded-lg border p-3" style={{ borderColor: 'var(--app-border)', background: 'var(--app-bg)' }}>
                <p className="text-sm mb-3" style={{ color: 'var(--app-text)' }}>
                  Подключите свой Telegram, чтобы не проверять раздел «Записи» вручную.
                </p>
                {tg.link_url ? (
                  <a href={tg.link_url} target="_blank" rel="noopener noreferrer" className="block">
                    <Button data-testid="telegram-connect-btn" size="sm" className="w-full text-white" style={{ background: 'var(--app-accent)' }}>
                      Подключить Telegram <ExternalLink size={14} className="ml-1.5" aria-hidden="true" />
                    </Button>
                  </a>
                ) : (
                  <Button size="sm" variant="outline" className="w-full" onClick={() => refreshTelegramStatus(true)}>
                    Получить ссылку подключения
                  </Button>
                )}
                <p className="mt-2 text-xs" style={{ color: 'var(--app-text-muted)' }}>
                  Ссылка действует 15 минут. В открывшемся боте нажмите «Запустить».
                </p>
              </div>
            )}
          </div>
          {/* Google Calendar */}
          <div id="google-calendar" className="scroll-mt-6">
            <label className="text-stone-400 text-sm font-medium mb-2 block">Google Calendar</label>
            <p className="mb-3 text-xs leading-relaxed text-stone-500">
              ProPraxis учитывает занятые интервалы основного календаря и управляет только событиями записей, созданными сервисом.{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:underline">
                Как используются данные
              </a>
            </p>
            {!gcal.available ? <p className="text-stone-500 text-xs">Интеграция не настроена администратором</p>
             : gcal.connected ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 rounded-lg bg-green-900/20 border border-green-900/40">
                  <div><p className="text-green-400 text-sm font-medium">Подключён</p><p className="text-stone-500 text-xs">{gcal.email}</p></div>
                  <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300"
                    onClick={async () => {
                      try {
                        await api.delete('/oauth/calendar/disconnect');
                        setGcal({ ...gcal, connected: false, email: '', requires_reconnect: false, last_error: '' });
                        toast.success('Google Calendar отключён');
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Не удалось отключить Google Calendar');
                      }
                    }}>Отключить</Button>
                </div>
                {gcal.last_error && <p className="text-amber-400 text-xs">{gcal.last_error}</p>}
                {gcal.requires_reconnect && (
                  <Button size="sm" className="w-full bg-amber-700 hover:bg-amber-600 text-white" onClick={connectGoogle}>
                    Переподключить Google Calendar
                  </Button>
                )}
                <Button size="sm" className="w-full bg-stone-800 hover:bg-stone-700 text-stone-300" data-testid="gcal-sync-all-btn"
                  onClick={async () => {
                    try { toast.info('Синхронизация записей...');
                      const { data } = await api.post('/oauth/calendar/sync-all');
                      if (data.synced > 0) toast.success(`Синхронизировано: ${data.synced} из ${data.total}`);
                      else if (data.total === 0) toast.info('Все записи уже синхронизированы');
                      else toast.warning(`Ошибки: ${data.errors} из ${data.total}`);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Ошибка синхронизации');
                      api.get('/oauth/calendar/status').then(({ data }) => setGcal(data)).catch(() => {});
                    }
                  }}>Синхронизировать все записи</Button>
              </div>
            ) : (
              <Button size="sm" className="bg-stone-800 hover:bg-stone-700 text-stone-300"
                onClick={connectGoogle}>Подключить Google Calendar</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ThemeSelector() {
  const { theme, setTheme } = useTheme();
  const options = [
    { id: 'light', icon: Sun, label: 'Светлая' },
    { id: 'dark', icon: Moon, label: 'Тёмная' },
    { id: 'system', icon: Monitor, label: 'Системная' },
  ];
  return (
    <div>
      <label className="text-stone-400 text-sm font-medium mb-2 block">Тема оформления</label>
      <div className="flex gap-2">
        {options.map(o => (
          <button key={o.id} onClick={() => setTheme(o.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm border transition-all ${
              theme === o.id ? 'bg-amber-700/15 border-amber-700/50 text-amber-500' : 'bg-stone-950/50 border-stone-800 text-stone-500 hover:border-stone-700'
            }`}>
            <o.icon size={15} /><span>{o.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}


/* ── Doctors Section (CLINIC only) ── */
function DoctorsSection({ doctors, setDoctors, newDoctor, setNewDoctor, doctorSaving, setDoctorSaving, teamLimits, setTeamLimits }) {
  return (
    <Card className="border-stone-800 bg-stone-900/60">
      <CardHeader>
        <CardTitle className="text-stone-200 flex items-center gap-2">
          <Stethoscope size={20} className="text-teal-500" />
          Врачи клиники
        </CardTitle>
        <p className="text-xs text-stone-500">Врачи — специалисты, которые ведут приём. У каждого врача своё расписание и пациенты.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Doctor limit */}
        <div className="flex items-center gap-3">
          <div className="text-sm text-stone-400">
            Врачей: <span className="font-semibold text-stone-200">{teamLimits.current_doctors}</span> / <span className="font-semibold text-stone-200">{teamLimits.max_doctors ?? '∞'}</span>
          </div>
          {teamLimits.max_doctors !== null && teamLimits.current_doctors >= teamLimits.max_doctors && (
            <Badge data-testid="doctor-limit-badge" className="bg-teal-900/40 text-teal-400 border-teal-800/40 text-[10px]">Лимит достигнут</Badge>
          )}
        </div>

        {/* Add doctor form */}
        {teamLimits.max_doctors !== null && teamLimits.current_doctors >= teamLimits.max_doctors ? (
          <div data-testid="doctor-limit-notice" className="p-4 rounded-lg border border-teal-800/40 bg-teal-950/20 text-center space-y-2">
            <p className="text-sm text-teal-400">Достигнут лимит врачей на тарифе «{teamLimits.plan_name}»</p>
            <p className="text-xs text-stone-500">Обратитесь в поддержку для расширения</p>
          </div>
        ) : (
          <div className="p-4 rounded-lg border border-stone-800 bg-stone-950/40 space-y-3">
            <p className="text-sm font-medium text-stone-300">Добавить врача</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input data-testid="doctor-name-input" placeholder="Имя" value={newDoctor.name}
                onChange={e => setNewDoctor(d => ({ ...d, name: e.target.value }))}
                className="bg-stone-800 border-stone-700 text-stone-200 placeholder:text-stone-600" />
              <Input data-testid="doctor-email-input" placeholder="Email" type="email" value={newDoctor.email}
                onChange={e => setNewDoctor(d => ({ ...d, email: e.target.value }))}
                className="bg-stone-800 border-stone-700 text-stone-200 placeholder:text-stone-600" />
              <Input data-testid="doctor-password-input" placeholder="Пароль" type="password" value={newDoctor.password}
                onChange={e => setNewDoctor(d => ({ ...d, password: e.target.value }))}
                className="bg-stone-800 border-stone-700 text-stone-200 placeholder:text-stone-600" />
              <Input data-testid="doctor-specialization-input" placeholder="Специализация" value={newDoctor.specialization}
                onChange={e => setNewDoctor(d => ({ ...d, specialization: e.target.value }))}
                className="bg-stone-800 border-stone-700 text-stone-200 placeholder:text-stone-600" />
            </div>
            <Button data-testid="add-doctor-btn" size="sm" disabled={doctorSaving || !newDoctor.name || !newDoctor.email || !newDoctor.password}
              className="bg-teal-700 hover:bg-teal-600 text-white"
              onClick={async () => {
                setDoctorSaving(true);
                try {
                  const { data } = await api.post('/team/doctors', newDoctor);
                  setDoctors(prev => [...prev, data]);
                  setNewDoctor({ name: '', email: '', password: '', specialization: '' });
                  setTeamLimits(prev => ({ ...prev, current_doctors: prev.current_doctors + 1 }));
                  toast.success(`Врач ${data.name} добавлен`);
                } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
                setDoctorSaving(false);
              }}>
              <Plus size={14} className="mr-1" />{doctorSaving ? 'Добавление...' : 'Добавить'}
            </Button>
          </div>
        )}

        {/* Doctor list */}
        {doctors.length === 0 ? (
          <p className="text-center text-stone-600 py-6 text-sm">Врачей пока нет</p>
        ) : (
          <div className="space-y-3">
            {doctors.map(doc => (
              <div key={doc.id} data-testid={`doctor-row-${doc.id}`}
                className="p-4 rounded-lg border border-stone-800 bg-stone-950/30 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-teal-900/30 flex items-center justify-center shrink-0">
                  <Stethoscope size={18} className="text-teal-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-200">{doc.name}</p>
                  <p className="text-xs text-stone-500">{doc.email}</p>
                  {doc.specialization && <p className="text-xs text-teal-500/80 mt-0.5">{doc.specialization}</p>}
                </div>
                <Button data-testid={`remove-doctor-${doc.id}`} size="sm" variant="ghost"
                  className="text-red-400/60 hover:text-red-400 hover:bg-red-900/20"
                  onClick={async () => {
                    try {
                      await api.delete(`/team/doctors/${doc.id}`);
                      setDoctors(prev => prev.filter(d => d.id !== doc.id));
                      setTeamLimits(prev => ({ ...prev, current_doctors: Math.max(0, prev.current_doctors - 1) }));
                      toast.success(`Врач ${doc.name} удалён`);
                    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
                  }}>
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


/* ── Admins Section ── */
function AdminsSection({ admins, setAdmins, newAdmin, setNewAdmin, adminSaving, setAdminSaving, teamLimits, setTeamLimits, isClinic, navigate }) {
  return (
    <Card className="border-stone-800 bg-stone-900/60">
      <CardHeader>
        <CardTitle className="text-stone-200 flex items-center gap-2">
          <Users size={20} className="text-amber-500" />
          Администраторы
        </CardTitle>
        <p className="text-xs text-stone-500">Администраторы получают доступ только к выбранным модулям. Заметки к сеансам всегда доступны только врачу.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Admin limit indicator */}
        <div className="flex items-center gap-3">
          <div className="text-sm text-stone-400">
            Администраторов: <span className="font-semibold text-stone-200">{teamLimits.current_admins}</span> / <span className="font-semibold text-stone-200">{teamLimits.max_admins ?? '∞'}</span>
          </div>
          {teamLimits.max_admins !== null && teamLimits.current_admins >= teamLimits.max_admins && (
            <Badge data-testid="admin-limit-badge" className="bg-amber-900/40 text-amber-400 border-amber-800/40 text-[10px]">Лимит достигнут</Badge>
          )}
        </div>

        {/* Add admin form */}
        {teamLimits.max_admins !== null && teamLimits.current_admins >= teamLimits.max_admins ? (
          <div data-testid="admin-limit-notice" className="p-4 rounded-lg border border-amber-800/40 bg-amber-950/20 text-center space-y-2">
            <p className="text-sm text-amber-400">Достигнут лимит тарифа «{teamLimits.plan_name}»</p>
            <p className="text-xs text-stone-500">Обратитесь в поддержку для расширения или обновите тариф</p>
            <Button data-testid="upgrade-plan-btn" size="sm" onClick={() => navigate('/pricing')}
              className="bg-amber-600 hover:bg-amber-700 text-white mt-1">Обновить тариф</Button>
          </div>
        ) : (
          <div className="p-4 rounded-lg border border-stone-800 bg-stone-950/40 space-y-3">
            <p className="text-sm font-medium text-stone-300">Добавить администратора</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Input data-testid="admin-name-input" placeholder="Имя" value={newAdmin.name}
                onChange={e => setNewAdmin(a => ({ ...a, name: e.target.value }))}
                className="bg-stone-800 border-stone-700 text-stone-200 placeholder:text-stone-600" />
              <Input data-testid="admin-email-input" placeholder="Email" type="email" value={newAdmin.email}
                onChange={e => setNewAdmin(a => ({ ...a, email: e.target.value }))}
                className="bg-stone-800 border-stone-700 text-stone-200 placeholder:text-stone-600" />
              <Input data-testid="admin-password-input" placeholder="Пароль" type="password" value={newAdmin.password}
                onChange={e => setNewAdmin(a => ({ ...a, password: e.target.value }))}
                className="bg-stone-800 border-stone-700 text-stone-200 placeholder:text-stone-600" />
            </div>
            <div className="flex flex-wrap gap-4 py-1">
              {[
                { key: 'can_manage_calendar', label: 'Календарь и Очередь' },
                { key: 'can_manage_patients', label: 'База клиентов' },
                { key: 'can_manage_finances', label: 'Финансы' },
                ...(isClinic ? [{ key: 'can_manage_rooms', label: 'Кабинеты' }] : []),
              ].map(p => (
                <label key={p.key} className="flex items-center gap-2 cursor-pointer text-sm text-stone-400">
                  <input data-testid={`new-admin-perm-${p.key}`} type="checkbox"
                    checked={newAdmin.permissions?.[p.key] !== false}
                    onChange={e => setNewAdmin(a => ({
                      ...a,
                      permissions: {
                        ...(a.permissions || { can_manage_calendar: true, can_manage_patients: true, can_manage_finances: true, can_manage_rooms: false }),
                        [p.key]: e.target.checked
                      }
                    }))}
                    className="w-4 h-4 rounded border-stone-700 bg-stone-800 accent-amber-500" />
                  {p.label}
                </label>
              ))}
            </div>
            <Button data-testid="add-admin-btn" size="sm" disabled={adminSaving || !newAdmin.name || !newAdmin.email || !newAdmin.password}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={async () => {
                setAdminSaving(true);
                try {
                  const payload = {
                    name: newAdmin.name, email: newAdmin.email, password: newAdmin.password,
                    permissions: newAdmin.permissions || { can_manage_calendar: true, can_manage_patients: true, can_manage_finances: true, can_manage_rooms: false },
                  };
                  const { data } = await api.post('/team/admins', payload);
                  setAdmins(prev => [...prev, data]);
                  setNewAdmin({ name: '', email: '', password: '', permissions: null });
                  setTeamLimits(prev => ({ ...prev, current_admins: prev.current_admins + 1 }));
                  toast.success(`Администратор ${data.name} добавлен`);
                } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
                setAdminSaving(false);
              }}>
              <Plus size={14} className="mr-1" />{adminSaving ? 'Добавление...' : 'Добавить'}
            </Button>
          </div>
        )}

        {/* Admin list */}
        {admins.length === 0 ? (
          <p className="text-center text-stone-600 py-6 text-sm">Администраторов пока нет</p>
        ) : (
          <div className="space-y-3">
            {admins.map(admin => (
              <div key={admin.id} data-testid={`admin-row-${admin.id}`}
                className="p-4 rounded-lg border border-stone-800 bg-stone-950/30 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-stone-200">{admin.name}</p>
                    <p className="text-xs text-stone-500">{admin.email}</p>
                  </div>
                  <Button data-testid={`remove-admin-${admin.id}`} size="sm" variant="ghost"
                    className="text-red-400/60 hover:text-red-400 hover:bg-red-900/20"
                    onClick={async () => {
                      try {
                        await api.delete(`/team/admins/${admin.id}`);
                        setAdmins(prev => prev.filter(a => a.id !== admin.id));
                        setTeamLimits(prev => ({ ...prev, current_admins: Math.max(0, prev.current_admins - 1) }));
                        toast.success(`Администратор ${admin.name} удалён`);
                      } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
                    }}>
                    <Trash2 size={14} />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-4">
                  {[
                    { key: 'can_manage_calendar', label: 'Календарь и Очередь' },
                    { key: 'can_manage_patients', label: 'База клиентов' },
                    { key: 'can_manage_finances', label: 'Финансы' },
                    ...(isClinic ? [{ key: 'can_manage_rooms', label: 'Кабинеты' }] : []),
                  ].map(p => (
                    <label key={p.key} className="flex items-center gap-2 cursor-pointer text-sm text-stone-400">
                      <input data-testid={`admin-perm-${admin.id}-${p.key}`} type="checkbox"
                        checked={(admin.permissions || {})[p.key] !== false}
                        onChange={async e => {
                          const newPerms = { ...(admin.permissions || {}), [p.key]: e.target.checked };
                          try {
                            const { data } = await api.put(`/team/admins/${admin.id}`, { permissions: newPerms });
                            setAdmins(prev => prev.map(a => a.id === admin.id ? data : a));
                            toast.success('Права обновлены');
                          } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
                        }}
                        className="w-4 h-4 rounded border-stone-700 bg-stone-800 accent-amber-500" />
                      {p.label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


/* ── Rooms Tab ── */
function RoomsTab({ rooms, setRooms, doctors, isOwner, onEdit, onAdd }) {
  const doctorMap = {};
  doctors.forEach(d => { doctorMap[d.id] = d; });

  return (
    <Card className="border-stone-800 bg-stone-900/60">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-stone-200 flex items-center gap-2">
            <DoorOpen size={20} className="text-violet-500" />
            Кабинеты
          </CardTitle>
          <p className="text-xs text-stone-500 mt-1">Управляйте физическими кабинетами и назначайте их врачам</p>
        </div>
        <Button data-testid="add-room-btn" size="sm" onClick={onAdd} className="bg-violet-700 hover:bg-violet-600 text-white">
          <Plus size={16} className="mr-1" /> Добавить
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {rooms.length === 0 ? (
          <p className="text-center text-stone-600 py-8 text-sm">Кабинетов пока нет</p>
        ) : (
          rooms.map(room => (
            <div key={room.id} data-testid={`room-row-${room.id}`}
              className="p-4 rounded-lg border border-stone-800 bg-stone-950/30 space-y-3 hover:border-stone-700 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-violet-900/30 flex items-center justify-center shrink-0">
                    <DoorOpen size={18} className="text-violet-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-stone-200">{room.name}</p>
                    {room.description && <p className="text-xs text-stone-500 mt-0.5">{room.description}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button data-testid={`edit-room-${room.id}`} size="sm" variant="ghost"
                    className="text-stone-500 hover:text-amber-500 h-8 w-8 p-0"
                    onClick={() => onEdit(room)}>
                    <Pencil size={14} />
                  </Button>
                  <Button data-testid={`delete-room-${room.id}`} size="sm" variant="ghost"
                    className="text-stone-500 hover:text-red-400 h-8 w-8 p-0"
                    onClick={async () => {
                      try {
                        await api.delete(`/rooms/${room.id}`);
                        setRooms(prev => prev.filter(r => r.id !== room.id));
                        toast.success('Кабинет удалён');
                      } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
                    }}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
              {/* Assigned doctors */}
              {(room.assigned_doctor_ids || []).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {room.assigned_doctor_ids.map(did => {
                    const doc = doctorMap[did];
                    return (
                      <Badge key={did} className="bg-teal-900/30 text-teal-400 border-teal-800/40 text-[10px]">
                        <Stethoscope size={10} className="mr-1" />
                        {doc ? doc.name : did}
                      </Badge>
                    );
                  })}
                </div>
              )}
              {/* Room settings badges */}
              <div className="flex items-center gap-2">
                <Badge className={`text-[10px] ${room.admin_can_manage ? 'bg-green-900/30 text-green-400 border-green-800/40' : 'bg-stone-800/50 text-stone-600 border-stone-700/40'}`}>
                  {room.admin_can_manage ? 'Админы управляют' : 'Только владелец'}
                </Badge>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}


function ClinicPageTab() {
  const [cp, setCp] = useState({
    slug: '', clinic_name: '', hero_title: '', hero_subtitle: '', about: '',
    license_number: '', license_date: '', address: '', phone: '', email: '',
    working_hours_text: '', inn: '', ogrn: '', privacy_url: '', offer_url: '',
    map_embed: '', theme: 'forest', logo: '', hero_bg: '',
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/clinic-page').then(({ data }) => {
      if (data && typeof data === 'object') setCp(prev => ({ ...prev, ...data }));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { logo, hero_bg, ...rest } = cp;
      await api.put('/clinic-page', rest);
      toast.success('Сайт клиники сохранён');
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
    setSaving(false);
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.put('/clinic-page/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCp(prev => ({ ...prev, logo: data.logo }));
      toast.success('Логотип загружен');
    } catch { toast.error('Ошибка загрузки'); }
  };

  const uploadHeroBg = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.put('/clinic-page/hero-bg', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCp(prev => ({ ...prev, hero_bg: data.hero_bg }));
      toast.success('Фон загружен');
    } catch { toast.error('Ошибка загрузки'); }
  };

  const BASE = process.env.REACT_APP_BACKEND_URL;

  if (loading) return <p className="text-stone-500 text-sm">Загрузка...</p>;

  const Field = ({ label, field, placeholder, textarea }) => (
    <div>
      <label className="text-stone-400 text-xs font-medium mb-1.5 block">{label}</label>
      {textarea ? (
        <textarea value={cp[field] || ''} onChange={e => setCp(prev => ({ ...prev, [field]: e.target.value }))}
          placeholder={placeholder} rows={3}
          className="w-full rounded-md bg-stone-950/50 border border-stone-800 text-stone-200 placeholder:text-stone-600 p-2 text-sm resize-none"
        />
      ) : (
        <Input value={cp[field] || ''} onChange={e => setCp(prev => ({ ...prev, [field]: e.target.value }))}
          placeholder={placeholder}
          className="bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-600"
        />
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* URL & Preview */}
      <Card className="bg-stone-900 border-stone-800">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-stone-200 text-base">Адрес и предпросмотр</CardTitle>
            {cp.slug && (
              <a href={`/organization/${cp.slug}`} target="_blank" rel="noopener noreferrer"
                className="text-xs text-amber-500 hover:text-amber-400 flex items-center gap-1">
                Открыть сайт →
              </a>
            )}
          </div>
          <Field label="URL-адрес (slug)" field="slug" placeholder="ivanova-clinic" />
          {cp.slug && <p className="text-xs text-stone-500">{BASE}/organization/{cp.slug}</p>}
        </CardContent>
      </Card>

      {/* Hero Section */}
      <Card className="bg-stone-900 border-stone-800">
        <CardContent className="p-6 space-y-4">
          <CardTitle className="text-stone-200 text-base">Hero-секция</CardTitle>
          <Field label="Название клиники" field="clinic_name" placeholder="Психологический кабинет доктора Ивановой" />
          <Field label="Заголовок" field="hero_title" placeholder="Бережное пространство для вашего здоровья" />
          <Field label="Подзаголовок" field="hero_subtitle" placeholder="Доказательная психотерапия" />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-stone-400 text-xs font-medium mb-1.5 block">Логотип</label>
              <div className="flex items-center gap-3">
                {cp.logo && <img src={`${BASE}/api/clinic-logos/${cp.logo}`} alt="" className="w-10 h-10 rounded-lg object-cover border border-stone-700" />}
                <label className="cursor-pointer text-xs text-amber-500 hover:text-amber-400">
                  {cp.logo ? 'Заменить' : 'Загрузить'}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
                </label>
              </div>
            </div>
            <div>
              <label className="text-stone-400 text-xs font-medium mb-1.5 block">Фон hero</label>
              <div className="flex items-center gap-3">
                {cp.hero_bg && <img src={`${BASE}/api/hero-bgs/${cp.hero_bg}`} alt="" className="w-16 h-10 rounded object-cover border border-stone-700" />}
                <label className="cursor-pointer text-xs text-amber-500 hover:text-amber-400">
                  {cp.hero_bg ? 'Заменить' : 'Загрузить'}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadHeroBg} />
                </label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* About & License */}
      <Card className="bg-stone-900 border-stone-800">
        <CardContent className="p-6 space-y-4">
          <CardTitle className="text-stone-200 text-base">О клинике и лицензии</CardTitle>
          <Field label="О клинике" field="about" placeholder="Описание вашей клиники..." textarea />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Номер лицензии" field="license_number" placeholder="ЛО-77-01-123456" />
            <Field label="Дата лицензии" field="license_date" placeholder="15.03.2024" />
          </div>
        </CardContent>
      </Card>

      {/* Contacts & Footer */}
      <Card className="bg-stone-900 border-stone-800">
        <CardContent className="p-6 space-y-4">
          <CardTitle className="text-stone-200 text-base">Контакты и подвал</CardTitle>
          <Field label="Адрес" field="address" placeholder="Москва, ул. Тверская, д. 15" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Телефон" field="phone" placeholder="+7 (495) 123-45-67" />
            <Field label="Email" field="email" placeholder="info@clinic.ru" />
          </div>
          <Field label="Режим работы" field="working_hours_text" placeholder="Пн-Пт: 10:00 — 21:00" textarea />
          <Field label="Ссылка Яндекс.Карты (embed)" field="map_embed" placeholder="https://yandex.ru/map-widget/v1/..." />
        </CardContent>
      </Card>

      {/* Legal */}
      <Card className="bg-stone-900 border-stone-800">
        <CardContent className="p-6 space-y-4">
          <CardTitle className="text-stone-200 text-base">Юридическая информация</CardTitle>
          <div className="grid grid-cols-2 gap-4">
            <Field label="ИНН" field="inn" placeholder="7701234567" />
            <Field label="ОГРН" field="ogrn" placeholder="1177746123456" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Ссылка на политику конфиденциальности" field="privacy_url" placeholder="https://..." />
            <Field label="Ссылка на оферту" field="offer_url" placeholder="https://..." />
          </div>
        </CardContent>
      </Card>

      {/* Theme */}
      <Card className="bg-stone-900 border-stone-800">
        <CardContent className="p-6 space-y-4">
          <CardTitle className="text-stone-200 text-base">Тема оформления</CardTitle>
          <Select value={cp.theme || 'forest'} onValueChange={v => setCp(prev => ({ ...prev, theme: v }))}>
            <SelectTrigger className="bg-stone-950/50 border-stone-800 text-stone-200 w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-stone-900 border-stone-800">
              <SelectItem value="forest">Хвойный лес</SelectItem>
              <SelectItem value="sand">Тёплый песок</SelectItem>
              <SelectItem value="lavender">Лавандовый туман</SelectItem>
              <SelectItem value="ocean">Океанский бриз</SelectItem>
              <SelectItem value="rose">Пудровая роза</SelectItem>
              <SelectItem value="indigo">Тёмный индиго</SelectItem>
              <SelectItem value="mint">Мятная свежесть</SelectItem>
              <SelectItem value="graphite">Тёплый графит</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Custom Domain */}
      <Card className="bg-stone-900 border-stone-800">
        <CardContent className="p-6 space-y-4">
          <CardTitle className="text-stone-200 text-base">Свой домен</CardTitle>
          <p className="text-stone-500 text-xs leading-relaxed">
            Привяжите свой домен к сайту клиники. Посетители будут видеть только ваш сайт, без ProPraxis в адресной строке.
          </p>
          <Field label="Домен" field="custom_domain" placeholder="massage-vip.ru" />
          {cp.custom_domain && (
            <div className="p-3 rounded-lg bg-stone-950/50 border border-stone-800 space-y-2">
              <p className="text-xs font-medium text-amber-400">Настройка DNS</p>
              <p className="text-xs text-stone-400">Добавьте A-запись в DNS вашего домена:</p>
              <div className="flex items-center gap-2 bg-stone-900 rounded px-3 py-2 font-mono text-xs text-stone-300">
                <span className="text-stone-500">A</span>
                <span className="text-stone-300">{cp.custom_domain}</span>
                <span className="text-stone-500">→</span>
                <span className="text-amber-400">158.160.236.129</span>
              </div>
              <p className="text-[10px] text-stone-600">После добавления записи, SSL-сертификат будет выпущен автоматически (до 5 минут).</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Button data-testid="save-clinic-page" onClick={save} disabled={saving}
        className="bg-amber-700 hover:bg-amber-600 text-white w-full h-11">
        <Save size={16} className="mr-2" /> {saving ? 'Сохранение...' : 'Сохранить сайт клиники'}
      </Button>
    </div>
  );
}


function ExtraServicesTab() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestOpen, setRequestOpen] = useState(null);
  const [reqForm, setReqForm] = useState({ name: '', phone: '', email: '', comment: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/extra-services').then(({ data }) => setServices(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const submitRequest = async () => {
    if (!reqForm.name || !reqForm.phone) { toast.error('Заполните имя и телефон'); return; }
    setSubmitting(true);
    try {
      await api.post('/extra-services/request', { ...reqForm, service_id: requestOpen });
      toast.success('Заявка отправлена! Мы свяжемся с вами.');
      setRequestOpen(null);
      setReqForm({ name: '', phone: '', email: '', comment: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
    setSubmitting(false);
  };

  if (loading) return <p className="text-stone-500 text-sm">Загрузка...</p>;

  return (
    <div className="space-y-4">
      <p className="text-stone-400 text-sm">Дополнительные услуги для развития вашей клиники</p>
      {services.map(s => (
        <Card key={s.id} className="bg-stone-900 border-stone-800">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className="text-stone-200 text-sm font-semibold mb-1">{s.name}</h3>
                <p className="text-stone-500 text-xs leading-relaxed mb-3">{s.description}</p>
                <Badge className="bg-amber-900/20 text-amber-400 border-amber-900/40 text-xs">{s.price_range}</Badge>
              </div>
              <Button data-testid={`request-${s.id}`} size="sm" onClick={() => { setRequestOpen(s.id); setReqForm({ name: '', phone: '', email: '', comment: '' }); }}
                className="bg-amber-700 hover:bg-amber-600 text-white shrink-0">
                Оставить заявку
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}

      <Dialog open={!!requestOpen} onOpenChange={() => setRequestOpen(null)}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-sm" aria-describedby="svc-req-desc">
          <DialogHeader>
            <DialogTitle className="text-stone-200">Заявка на услугу</DialogTitle>
          </DialogHeader>
          <p id="svc-req-desc" className="text-xs text-stone-500 mb-4">
            {services.find(s => s.id === requestOpen)?.name}
          </p>
          <div className="space-y-3">
            <Input value={reqForm.name} onChange={e => setReqForm(f => ({...f, name: e.target.value}))}
              placeholder="Ваше имя" className="bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-600" />
            <Input value={reqForm.phone} onChange={e => setReqForm(f => ({...f, phone: e.target.value}))}
              placeholder="Телефон" className="bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-600" />
            <Input value={reqForm.email} onChange={e => setReqForm(f => ({...f, email: e.target.value}))}
              placeholder="Email (необязательно)" className="bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-600" />
            <Input value={reqForm.comment} onChange={e => setReqForm(f => ({...f, comment: e.target.value}))}
              placeholder="Комментарий" className="bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-600" />
            <Button data-testid="submit-service-request" onClick={submitRequest} disabled={submitting || !reqForm.name || !reqForm.phone}
              className="w-full bg-amber-700 hover:bg-amber-600 text-white">
              {submitting ? 'Отправка...' : 'Отправить заявку'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ProfileRulesSection() {
  const [rules, setRules] = useState({
    allow_contacts: false, allow_custom_prices: true, allow_reviews: true,
    allow_faq: true, allow_certificates: true, allow_bio: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/doctor-profile-rules').then(({ data }) => setRules(r => ({ ...r, ...data }))).catch(() => {});
  }, []);

  const save = async (field, value) => {
    const updated = { ...rules, [field]: value };
    setRules(updated);
    setSaving(true);
    try {
      await api.put('/doctor-profile-rules', { [field]: value });
    } catch { toast.error('Ошибка сохранения'); }
    setSaving(false);
  };

  const RULE_ITEMS = [
    { field: 'allow_contacts', label: 'Контакты', desc: 'Telegram, WhatsApp, телефон' },
    { field: 'allow_bio', label: 'Описание (О себе)', desc: 'Биография и специализация' },
    { field: 'allow_reviews', label: 'Отзывы', desc: 'Раздел отзывов на визитке' },
    { field: 'allow_faq', label: 'FAQ', desc: 'Часто задаваемые вопросы' },
    { field: 'allow_certificates', label: 'Сертификаты', desc: 'Дипломы и сертификаты' },
    { field: 'allow_custom_prices', label: 'Свои цены', desc: 'Показывать цены на визитке' },
  ];

  return (
    <Card className="bg-stone-900 border-stone-800">
      <CardContent className="p-5">
        <h3 className="text-stone-200 text-sm font-semibold mb-1 flex items-center gap-2">
          <Pencil size={14} className="text-amber-500" /> Правила визиток специалистов
        </h3>
        <p className="text-stone-600 text-xs mb-4">Что врачи могут показывать на своих публичных визитках</p>
        <div className="space-y-3">
          {RULE_ITEMS.map(({ field, label, desc }) => (
            <label key={field} className="flex items-center justify-between cursor-pointer">
              <div>
                <p className="text-stone-300 text-sm">{label}</p>
                <p className="text-stone-600 text-xs">{desc}</p>
              </div>
              <Switch checked={rules[field]} onCheckedChange={v => save(field, v)} />
            </label>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


function WorkloadLimitsCard({ settings }) {
  const [dailyH, setDailyH] = useState('');
  const [weeklyH, setWeeklyH] = useState('');
  const [dailyCount, setDailyCount] = useState('');
  const [weeklyCount, setWeeklyCount] = useState('');
  const [slotInterval, setSlotInterval] = useState('duration');
  const [bufferBefore, setBufferBefore] = useState('0');
  const [bufferAfter, setBufferAfter] = useState('0');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setDailyH(settings.max_daily_hours != null ? String(settings.max_daily_hours) : '');
      setWeeklyH(settings.max_weekly_hours != null ? String(settings.max_weekly_hours) : '');
      setDailyCount(settings.max_daily_appointments != null ? String(settings.max_daily_appointments) : '');
      setWeeklyCount(settings.max_weekly_appointments != null ? String(settings.max_weekly_appointments) : '');
      setSlotInterval(settings.slot_interval_minutes ? String(settings.slot_interval_minutes) : 'duration');
      setBufferBefore(String(settings.buffer_before_minutes || 0));
      setBufferAfter(String(settings.buffer_after_minutes || 0));
    }
  }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/settings', {
        max_daily_hours: dailyH ? parseFloat(dailyH) : 0,
        max_weekly_hours: weeklyH ? parseFloat(weeklyH) : 0,
        max_daily_appointments: dailyCount ? parseInt(dailyCount) : 0,
        max_weekly_appointments: weeklyCount ? parseInt(weeklyCount) : 0,
        slot_interval_minutes: slotInterval === 'duration' ? 0 : parseInt(slotInterval),
        buffer_before_minutes: parseInt(bufferBefore) || 0,
        buffer_after_minutes: parseInt(bufferAfter) || 0,
      });
      toast.success('Правила расписания сохранены');
    } catch { toast.error('Ошибка'); }
    setSaving(false);
  };

  return (
    <Card className="bg-stone-900 border-stone-800 mt-6">
      <CardContent className="p-6">
        <h3 className="text-stone-200 text-base font-semibold mb-1">Правила свободных окон</h3>
        <p className="text-stone-500 text-xs mb-4">
          Настройки применяются на странице записи и в ассистенте. Уже созданные встречи не изменятся.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
          <div>
            <label className="text-stone-400 text-xs mb-1.5 block">Начало встреч</label>
            <Select value={slotInterval} onValueChange={setSlotInterval}>
              <SelectTrigger data-testid="slot-interval" className="bg-stone-950/50 border-stone-800 text-stone-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-stone-900 border-stone-800">
                <SelectItem value="duration">По длительности встречи</SelectItem>
                {[5, 10, 15, 20, 30, 45, 60, 90, 120].map(value => (
                  <SelectItem key={value} value={String(value)}>
                    {value === 90 ? 'Каждые 1 ч 30 мин.' : value === 120 ? 'Каждые 2 ч.' : `Каждые ${value} мин.`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-stone-600 text-[10px] mt-1">
              Отсчёт идёт от начала рабочего интервала. Например, с 16:00 шаг 90 минут даст 16:00 и 17:30.
            </p>
          </div>
          <div>
            <label className="text-stone-400 text-xs mb-1.5 block">Перерыв до встречи, минут</label>
            <Input data-testid="buffer-before" type="number" step="5" min="0" max="240" value={bufferBefore}
              onChange={e => setBufferBefore(e.target.value)}
              className="bg-stone-950/50 border-stone-800 text-stone-200" />
          </div>
          <div>
            <label className="text-stone-400 text-xs mb-1.5 block">Перерыв после встречи, минут</label>
            <Input data-testid="buffer-after" type="number" step="5" min="0" max="240" value={bufferAfter}
              onChange={e => setBufferAfter(e.target.value)}
              className="bg-stone-950/50 border-stone-800 text-stone-200" />
          </div>
        </div>

        <div className="border-t border-stone-800/70 pt-4 mb-4">
          <p className="text-stone-300 text-sm font-medium mb-1">Лимиты загрузки</p>
          <p className="text-stone-600 text-xs mb-3">Когда достигнут любой лимит, новые слоты за этот период перестают показываться.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="text-stone-400 text-xs mb-1.5 block">Макс. часов в день</label>
            <Input data-testid="max-daily-hours" type="number" step="0.5" min="0" value={dailyH}
              onChange={e => setDailyH(e.target.value)} placeholder="Без ограничений"
              className="bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-600" />
          </div>
          <div>
            <label className="text-stone-400 text-xs mb-1.5 block">Макс. часов в неделю</label>
            <Input data-testid="max-weekly-hours" type="number" step="0.5" min="0" value={weeklyH}
              onChange={e => setWeeklyH(e.target.value)} placeholder="Без ограничений"
              className="bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-600" />
          </div>
          <div>
            <label className="text-stone-400 text-xs mb-1.5 block">Макс. встреч в день</label>
            <Input data-testid="max-daily-appointments" type="number" step="1" min="0" value={dailyCount}
              onChange={e => setDailyCount(e.target.value)} placeholder="Без ограничений"
              className="bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-600" />
          </div>
          <div>
            <label className="text-stone-400 text-xs mb-1.5 block">Макс. встреч в неделю</label>
            <Input data-testid="max-weekly-appointments" type="number" step="1" min="0" value={weeklyCount}
              onChange={e => setWeeklyCount(e.target.value)} placeholder="Без ограничений"
              className="bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-600" />
          </div>
        </div>
        <p className="text-stone-600 text-[10px] mb-3">
          Тариф также может ограничивать часы (Старт: 5ч/нед, Практик: 20ч/нед). Применяется более строгое ограничение.
        </p>
        <Button data-testid="save-limits-btn" size="sm" onClick={save} disabled={saving}
          className="bg-amber-700 hover:bg-amber-600 text-white">
          <Save size={14} className="mr-1.5" /> {saving ? '...' : 'Сохранить лимиты'}
        </Button>
      </CardContent>
    </Card>
  );
}


/* ── Audit Log Tab (Owner only) ── */
function AuditLogTab() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const limit = 50;

  useEffect(() => {
    setLoading(true);
    api.get(`/audit?limit=${limit}&offset=${page * limit}`)
      .then(({ data }) => { setEntries(data.entries || []); setTotal(data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page]);

  const actionLabels = {
    cancel: { label: 'Отмена', cls: 'bg-red-900/30 text-red-400 border-red-900/50' },
    delete: { label: 'Удаление', cls: 'bg-red-900/30 text-red-400 border-red-900/50' },
    mark_paid: { label: 'Оплата', cls: 'bg-green-900/30 text-green-400 border-green-900/50' },
    status_change: { label: 'Статус', cls: 'bg-amber-900/30 text-amber-400 border-amber-900/50' },
    create: { label: 'Создание', cls: 'bg-sky-900/30 text-sky-400 border-sky-900/50' },
    update: { label: 'Изменение', cls: 'bg-stone-800 text-stone-400 border-stone-700' },
  };
  const roleLabels = { owner: 'Владелец', admin: 'Админ', doctor: 'Специалист' };

  return (
    <Card className="bg-stone-900 border-stone-800">
      <CardHeader>
        <CardTitle className="text-stone-200 font-heading text-lg">Журнал действий</CardTitle>
        <p className="text-stone-500 text-xs">Все критические действия сотрудников фиксируются автоматически</p>
      </CardHeader>
      <CardContent>
        {loading ? <p className="text-stone-500 text-center py-6">Загрузка...</p> : entries.length === 0 ? (
          <p className="text-stone-600 text-center py-8">Журнал пуст</p>
        ) : (
          <div className="space-y-2">
            {entries.map(e => {
              const a = actionLabels[e.action] || actionLabels.update;
              return (
                <div key={e.id} className="flex items-start gap-3 p-3 rounded-lg bg-stone-950/30 border border-stone-800/50">
                  <Badge className={`${a.cls} border text-[10px] shrink-0 mt-0.5`}>{a.label}</Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-stone-200 text-sm">{e.details}</p>
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-stone-600">
                      <span>{e.user_name || 'Система'}</span>
                      <span className="px-1.5 py-0.5 rounded bg-stone-800">{roleLabels[e.user_role] || e.user_role}</span>
                      <span>{new Date(e.created_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    {e.before && e.after && (
                      <p className="text-[10px] text-stone-600 mt-1">
                        {Object.keys(e.after).map(k => `${k}: ${e.before[k] ?? '—'} → ${e.after[k]}`).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {total > limit && (
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-stone-800">
            <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => setPage(p => p - 1)}
              className="text-stone-400">Назад</Button>
            <span className="text-stone-600 text-xs">{page * limit + 1}–{Math.min((page + 1) * limit, total)} из {total}</span>
            <Button size="sm" variant="ghost" disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)}
              className="text-stone-400">Далее</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


/* ── Cyclic Schedule Section ── */
function CyclicScheduleSection({ settings }) {
  const [open, setOpen] = useState(false);
  const [cycle, setCycle] = useState(() => {
    return settings?.cyclic_schedule || { enabled: false, mode: 'simple', work_days: 2, off_days: 2, start_date: '', start_time: '09:00', end_time: '18:00', day_hours: [], pattern: [] };
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (settings?.cyclic_schedule) setCycle(settings.cyclic_schedule); }, [settings?.cyclic_schedule]);

  // Pattern mode: array of {type: 'work'|'off', start?, end?}
  const getPattern = () => cycle.pattern || [];
  const addPatternDay = (type) => {
    const p = [...getPattern(), { type, start: '09:00', end: '18:00' }];
    setCycle(c => ({ ...c, pattern: p }));
  };
  const removePatternDay = (idx) => setCycle(c => ({ ...c, pattern: (c.pattern || []).filter((_, i) => i !== idx) }));
  const updatePatternDay = (idx, field, val) => {
    const p = [...getPattern()]; p[idx] = { ...p[idx], [field]: val };
    setCycle(c => ({ ...c, pattern: p }));
  };

  // Simple mode: day_hours array
  const getDayHours = () => {
    const count = Math.ceil(cycle.work_days || 2);
    const existing = cycle.day_hours || [];
    return Array.from({ length: count }, (_, i) => existing[i] || { start: cycle.start_time || '09:00', end: cycle.end_time || '18:00' });
  };
  const updateDayHour = (idx, field, value) => {
    const hours = getDayHours(); hours[idx] = { ...hours[idx], [field]: value };
    setCycle(c => ({ ...c, day_hours: hours }));
  };

  const saveCycle = async () => {
    setSaving(true);
    try {
      const toSave = { ...cycle };
      if (cycle.mode !== 'pattern') toSave.day_hours = getDayHours();
      await api.put('/settings', { cyclic_schedule: toSave });
      toast.success('График сохранён');
    } catch { toast.error('Ошибка'); }
    setSaving(false);
  };

  // Preview 14 days
  const previewDays = () => {
    if (!cycle.enabled || !cycle.start_date) return [];
    const start = new Date(cycle.start_date + 'T00:00');
    const today = new Date();
    const days = [];
    for (let i = 0; i < 14; i++) {
      const d = new Date(today); d.setDate(d.getDate() + i);
      const totalDays = Math.round((d - start) / 86400000);
      let isWork = false, hours = null;
      if (cycle.mode === 'pattern' && getPattern().length > 0) {
        const patLen = getPattern().length;
        const pos = ((totalDays % patLen) + patLen) % patLen;
        const pd = getPattern()[pos];
        isWork = pd?.type === 'work';
        hours = isWork ? pd : null;
      } else {
        const cycleLen = (cycle.work_days || 2) + (cycle.off_days || 2);
        const pos = ((totalDays % cycleLen) + cycleLen) % cycleLen;
        isWork = pos < (cycle.work_days || 2);
        if (isWork) hours = getDayHours()[Math.floor(pos)] || null;
      }
      days.push({ date: d, isWork, hours });
    }
    return days;
  };

  return (
    <div className="mt-4">
      <button onClick={() => setOpen(!open)} className="text-stone-500 text-xs hover:text-amber-500 transition-colors flex items-center gap-1">
        {open ? '▾' : '▸'} Расширенные настройки расписания
      </button>
      {open && (
        <Card className="bg-stone-900 border-stone-800 mt-2">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-200 text-sm font-medium">Циклический график</p>
                <p className="text-stone-500 text-xs">2/2, 3/1, или произвольный паттерн (Р-Р-В-Р-Р-В-В)</p>
              </div>
              <Switch checked={cycle.enabled} onCheckedChange={async v => {
                setCycle(c => ({ ...c, enabled: v }));
                if (!v) {
                  try { await api.put('/settings', { cyclic_schedule: { ...cycle, enabled: false } }); toast.success('Циклический график отключён. Используется стандартное расписание.'); } catch {}
                }
              }} className="data-[state=checked]:bg-amber-700" />
            </div>

            {cycle.enabled && (
              <div className="space-y-3 pt-2 border-t border-stone-800/50">
                {/* Mode selector */}
                <div className="flex gap-2">
                  <button onClick={() => setCycle(c => ({ ...c, mode: 'simple' }))}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs border transition-all ${cycle.mode !== 'pattern' ? 'bg-amber-700/15 border-amber-700/50 text-amber-400' : 'bg-stone-950/50 border-stone-800 text-stone-500'}`}>
                    Простой (N через N)
                  </button>
                  <button onClick={() => setCycle(c => ({ ...c, mode: 'pattern' }))}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs border transition-all ${cycle.mode === 'pattern' ? 'bg-amber-700/15 border-amber-700/50 text-amber-400' : 'bg-stone-950/50 border-stone-800 text-stone-500'}`}>
                    Паттерн (произвольный)
                  </button>
                </div>

                <div>
                  <label className="text-stone-500 text-[10px] mb-1 block">Дата начала цикла</label>
                  <Input type="date" value={cycle.start_date} onChange={e => setCycle(c => ({ ...c, start_date: e.target.value }))}
                    className="bg-stone-950/50 border-stone-800 text-stone-200 h-8 w-48" />
                </div>

                {/* Simple mode */}
                {cycle.mode !== 'pattern' && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-stone-500 text-[10px] mb-1 block">Рабочих дней</label>
                        <Input type="number" min="0.5" max="14" step="0.5" value={cycle.work_days}
                          onChange={e => setCycle(c => ({ ...c, work_days: parseFloat(e.target.value) || 2 }))}
                          className="bg-stone-950/50 border-stone-800 text-stone-200 h-8" />
                      </div>
                      <div>
                        <label className="text-stone-500 text-[10px] mb-1 block">Выходных</label>
                        <Input type="number" min="0.5" max="14" step="0.5" value={cycle.off_days}
                          onChange={e => setCycle(c => ({ ...c, off_days: parseFloat(e.target.value) || 2 }))}
                          className="bg-stone-950/50 border-stone-800 text-stone-200 h-8" />
                      </div>
                    </div>
                    <div>
                      <label className="text-stone-500 text-[10px] mb-2 block">Часы по дням</label>
                      <div className="space-y-1.5">
                        {getDayHours().map((dh, i) => (
                          <div key={i} className="p-2 rounded bg-stone-950/30 border border-stone-800/50 space-y-1">
                            <span className="text-amber-400 text-xs font-medium">День {i + 1}</span>
                            {(dh.shifts || [{start: dh.start || '09:00', end: dh.end || '18:00'}]).map((shift, si) => (
                              <div key={si} className="flex items-center gap-1.5">
                                <Input type="time" value={shift.start} onChange={e => {
                                  const hours = getDayHours();
                                  const shifts = [...(hours[i].shifts || [{start: hours[i].start || '09:00', end: hours[i].end || '18:00'}])];
                                  shifts[si] = {...shifts[si], start: e.target.value};
                                  hours[i] = {...hours[i], shifts};
                                  setCycle(c => ({...c, day_hours: hours}));
                                }} className="bg-stone-950/50 border-stone-800 text-stone-200 h-7 w-[72px] text-xs" />
                                <span className="text-stone-600 text-[10px]">—</span>
                                <Input type="time" value={shift.end} onChange={e => {
                                  const hours = getDayHours();
                                  const shifts = [...(hours[i].shifts || [{start: hours[i].start || '09:00', end: hours[i].end || '18:00'}])];
                                  shifts[si] = {...shifts[si], end: e.target.value};
                                  hours[i] = {...hours[i], shifts};
                                  setCycle(c => ({...c, day_hours: hours}));
                                }} className="bg-stone-950/50 border-stone-800 text-stone-200 h-7 w-[72px] text-xs" />
                                {(dh.shifts || []).length > 1 && (
                                  <button onClick={() => {
                                    const hours = getDayHours();
                                    hours[i] = {...hours[i], shifts: (hours[i].shifts || []).filter((_, j) => j !== si)};
                                    setCycle(c => ({...c, day_hours: hours}));
                                  }} className="text-stone-700 hover:text-red-400"><X size={10} /></button>
                                )}
                              </div>
                            ))}
                            <button onClick={() => {
                              const hours = getDayHours();
                              const shifts = [...(hours[i].shifts || [{start: hours[i].start || '09:00', end: hours[i].end || '18:00'}]), {start: '14:00', end: '18:00'}];
                              hours[i] = {...hours[i], shifts};
                              setCycle(c => ({...c, day_hours: hours}));
                            }} className="text-stone-600 hover:text-amber-400 text-[9px]">+ смена / перерыв</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Pattern mode */}
                {cycle.mode === 'pattern' && (
                  <div className="space-y-2">
                    <p className="text-stone-500 text-[10px]">Задайте последовательность дней. Цикл повторяется бесконечно.</p>
                    <div className="space-y-1.5">
                      {getPattern().map((pd, i) => (
                        <div key={i} className={`flex items-center gap-2 p-2 rounded border ${pd.type === 'work' ? 'bg-amber-900/10 border-amber-800/30' : 'bg-stone-800/20 border-stone-800/40'}`}>
                          <span className={`text-xs font-bold w-5 text-center ${pd.type === 'work' ? 'text-amber-400' : 'text-stone-600'}`}>{i + 1}</span>
                          <select value={pd.type} onChange={e => updatePatternDay(i, 'type', e.target.value)}
                            className="bg-stone-950 border border-stone-800 text-stone-300 text-xs rounded px-2 py-1">
                            <option value="work">Рабочий</option>
                            <option value="off">Выходной</option>
                          </select>
                          {pd.type === 'work' && (
                            <div className="flex-1 space-y-1">
                              {(pd.shifts || [{start: pd.start || '09:00', end: pd.end || '18:00'}]).map((shift, si) => (
                                <div key={si} className="flex items-center gap-1.5">
                                  <Input type="time" value={shift.start} onChange={e => {
                                    const shifts = [...(pd.shifts || [{start: pd.start || '09:00', end: pd.end || '18:00'}])];
                                    shifts[si] = {...shifts[si], start: e.target.value};
                                    updatePatternDay(i, 'shifts', shifts);
                                  }} className="bg-stone-950/50 border-stone-800 text-stone-200 h-7 w-[72px] text-xs" />
                                  <span className="text-stone-600 text-[10px]">—</span>
                                  <Input type="time" value={shift.end} onChange={e => {
                                    const shifts = [...(pd.shifts || [{start: pd.start || '09:00', end: pd.end || '18:00'}])];
                                    shifts[si] = {...shifts[si], end: e.target.value};
                                    updatePatternDay(i, 'shifts', shifts);
                                  }} className="bg-stone-950/50 border-stone-800 text-stone-200 h-7 w-[72px] text-xs" />
                                  {(pd.shifts || []).length > 1 && (
                                    <button onClick={() => {
                                      const shifts = (pd.shifts || []).filter((_, j) => j !== si);
                                      updatePatternDay(i, 'shifts', shifts);
                                    }} className="text-stone-700 hover:text-red-400"><X size={10} /></button>
                                  )}
                                </div>
                              ))}
                              <button onClick={() => {
                                const shifts = [...(pd.shifts || [{start: pd.start || '09:00', end: pd.end || '18:00'}]), {start: '14:00', end: '18:00'}];
                                updatePatternDay(i, 'shifts', shifts);
                              }} className="text-stone-600 hover:text-amber-400 text-[9px]">+ смена</button>
                            </div>
                          )}
                          <button onClick={() => removePatternDay(i)} className="text-stone-600 hover:text-red-400 ml-auto"><X size={12} /></button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => addPatternDay('work')} className="border-amber-800/40 text-amber-400 text-xs h-7">+ Рабочий</Button>
                      <Button size="sm" variant="outline" onClick={() => addPatternDay('off')} className="border-stone-700 text-stone-500 text-xs h-7">+ Выходной</Button>
                    </div>
                    {getPattern().length > 0 && (
                      <p className="text-stone-600 text-[9px]">
                        Паттерн: {getPattern().map(p => p.type === 'work' ? 'Р' : 'В').join('-')} ({getPattern().length} дн.)
                      </p>
                    )}
                  </div>
                )}

                {/* Preview */}
                {cycle.start_date && (
                  <div>
                    <p className="text-stone-500 text-[10px] mb-1.5">Превью (14 дней):</p>
                    <div className="flex gap-1 flex-wrap">
                      {previewDays().map((d, i) => (
                        <div key={i} className={`w-12 rounded-md flex flex-col items-center justify-center py-1 text-[8px] border ${
                          d.isWork ? 'bg-amber-900/20 border-amber-800/40 text-amber-400' : 'bg-stone-800/30 border-stone-800/40 text-stone-600'
                        }`}>
                          <span className="font-bold text-[10px]">{d.date.getDate()}</span>
                          <span>{['Вс','Пн','Вт','Ср','Чт','Пт','Сб'][d.date.getDay()]}</span>
                          {d.hours && <span className="text-[7px] opacity-70">{(d.hours.start || '').slice(0,5)}-{(d.hours.end || '').slice(0,5)}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Button size="sm" onClick={saveCycle} disabled={saving} className="bg-amber-700 hover:bg-amber-600 text-white">
                  <Save size={14} className="mr-1.5" />{saving ? '...' : 'Сохранить'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
